import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    DatePicker,
    message,
    Breadcrumb,
    Divider,
    Row,
    Col,
    Spin,
    InputNumber,
    Select,
    Space,
    Popconfirm,
    Typography,
    Alert,
    Progress,
    Modal,
    TimePicker,
    Switch,
    TreeSelect,
    Popover,
    Radio,
    Upload,
    Steps,
    Tag,
    Collapse,
} from "antd";
import {
    SaveOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    UserOutlined,
    PlusOutlined,
    DeleteOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    DollarOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    FileSyncOutlined,
    CopyOutlined,
    SearchOutlined,
    FilterOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import {
    collectionAPIWithErrorHandling,
    collectionHelpers,
} from "../../../api/collection/collection";
import { collectionCategoryAPIWithErrorHandling } from "../../../api/collection/collectionCategory";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import { FixedSizeList as List } from 'react-window';
import { groupCustomerAPIWithErrorHandling } from "../../../api/groupCustomer/groupCustomer";
import { useSelector } from "react-redux";
import FeeTemplateModal from "../FeeTemplate/FeeTemplateModal";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import PaymentAccountSelector from "../PaymentAccount/PaymentAccountList";
import { cnfAPIWithErrorHandling } from "../../../api/invoice/cnf";
import * as XLSX from 'xlsx';
import ExcelJS from "exceljs";
import { downloadBlob } from "../../../utils/excel";
import { createTrace, timeAsync } from "../../../utils/perfTrace";
import "./CollectionForm.css";
import TransferContentInput from "./TransferContentInput";
import { assetAPIWithErrorHandling } from "../../../api/asset/asset";
import {
    useZeroFeeFilter,
    findZeroEntries,
    confirmZeroEntries,
    ZeroFeeFilterButton,
} from "./zeroFeeEntries";

const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Nhãn trạng thái khoản thu, dùng cho cột Trạng thái trong file Excel xuất ra. */
const STATUS_LABELS = {
    UNPAID: "Chưa thanh toán",
    PARTIAL: "Đã thu một phần",
    PAID: "Đã thanh toán",
    CANCELLED: "Đã hủy",
};

/**
 * Nhãn hiển thị trong file Excel, kèm mã ở cuối: "Tên tài sản [#3650]".
 *
 * Excel không có kiểu dropdown "hiện chữ, giữ mã ngầm" như web — ô chỉ lưu đúng chuỗi
 * người dùng chọn. Trước đây phải để mã ở một cột ẩn riêng, mà cột đó không đổi theo khi
 * người dùng chọn lại, nên tên và mã đá nhau. Nhét mã vào chính chuỗi thì chọn cái nào
 * là mã đi theo cái đó, không bao giờ lệch, và hai bản trùng tên cũng phân biệt được.
 */
const nhanCoMa = (ten, id) =>
    id === undefined || id === null || id === ""
        ? String(ten ?? "").trim()
        : `${String(ten ?? "").trim()} [#${id}]`;

/** Bóc nhãn "Tên [#12]" thành { ten, id }. Nhãn không mang mã (file cũ) thì id = null. */
const bocMa = (chuoi) => {
    const s = String(chuoi ?? "").trim();
    const khop = /^(.*?)\s*\[#(\d+)\]$/.exec(s);
    return khop ? { ten: khop[1].trim(), id: khop[2] } : { ten: s, id: null };
};

/**
 * Giữ tài sản đang gắn trên khoản thu trong danh sách lựa chọn của dòng.
 *
 * Tài sản đã chuyển sang chủ khác (sang tên căn hộ) thì không còn nằm trong
 * customer.assets của chủ cũ. Thiếu nó, ô Tài sản chỉ hiện id trần, file xuất bỏ trống
 * cột tài sản, và lúc lưu gửi chính con số id đó lên làm charge_target_number — backend
 * chặn "phải là chuỗi" cho cả đợt, kể cả khi dòng đó đã thanh toán và không sửa gì.
 */
const giuTaiSanCuaKhoanThu = (assets, entry) => {
    if (!entry?.asset_id || assets.some((a) => a.id === entry.asset_id)) {
        return assets;
    }
    return [
        ...assets,
        // Không rơi về String(asset_id): tên này đi thẳng vào charge_target_number lúc lưu,
        // lấy id làm tên là ghi đè tên tài sản trên khoản chưa thu thành một con số.
        { id: entry.asset_id, name: entry.asset_name || entry.charge_target_number || "", giuLai: true },
    ];
};

/**
 * Số thứ tự cột -> chữ cột Excel: 1=A … 26=Z, 27=AA, 28=AB.
 * Bản cũ dùng String.fromCharCode(64 + n) nên từ cột 27 trở đi sinh ra ký tự '[' và
 * làm hỏng công thức dropdown của cả file.
 */
const chuCot = (n) => {
    let s = "";
    let con = n;
    while (con > 0) {
        const du = (con - 1) % 26;
        s = String.fromCharCode(65 + du) + s;
        con = Math.floor((con - 1) / 26);
    }
    return s;
};


// ✅ Component riêng, tự quản lý state nội bộ, chỉ notify ra ngoài khi blur
// const FeeInputCell = React.memo(({ initialValue, disabled, onCommit }) => {
//     const [localVal, setLocalVal] = React.useState(initialValue ?? 0);

//     // Sync lại nếu initialValue thay đổi từ bên ngoài (load data, reset, copy)
//     React.useEffect(() => {
//         setLocalVal(initialValue ?? 0);
//     }, [initialValue]);

//     return (
//         <InputNumber
//             min={0}
//             max={999999999}
//             value={localVal}
//             formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
//             parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
//             placeholder="0"
//             className="w-full"
//             disabled={disabled}
//             style={{ width: "100%" }}
//             onChange={(val) => setLocalVal(val ?? 0)}
//             onBlur={() => onCommit(Number(localVal) || 0)}
//         />
//     );
// });

// ─── CollectionRow ────────────────────────────────────────────────────────────
// Component riêng cho từng dòng trong bảng để tối ưu hóa render
/**
 * Chiều cao phần thân bảng khoản thu, co theo cửa sổ trình duyệt.
 *
 * Trước đây cố định 500px: màn 24 inch thừa cả khoảng trắng bên dưới mà vẫn chỉ xem
 * được 7 dòng, còn laptop nhỏ thì bảng lại tràn quá tầm nhìn. 420px là phần chừa cho
 * đầu trang, thẻ bộ lọc và thanh tổng kết bên dưới.
 */
const computeTableHeight = () =>
  Math.max(320, Math.min(700, window.innerHeight - 420));

const CollectionRow = React.memo(({ index, style, data }) => {
  const { tableDataSource, columns } = data;
  const record = tableDataSource[index];
  if (!record) return null;
  if (record.isHeader) {
    return (
      <div className="collection-group-header" style={style}>
        {/* Dính trái khi cuộn ngang, không thì kéo sang cột phí là mất tên khu phố,
            chỉ còn trơ dải màu */}
        <span
          style={{
            position: "sticky",
            left: 0,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <EnvironmentOutlined />
          {record.orgName} ({record.itemCount} khách hàng)
        </span>
      </div>
    );
  }
  return (
    <div
      className={`collection-table-row ${index % 2 === 0 ? "collection-table-row-even" : "collection-table-row-odd"}`}
      style={style}
    >
      {columns.map((col, colIdx) => (
        <div
          key={colIdx}
          style={{
            width: typeof col.width === "string" ? col.width : `${col.width}px`,
            minWidth:
              typeof col.width === "string" ? col.width : `${col.width}px`,
            flexShrink: 0,
            padding: "0 8px",
            // Bắt buộc phải khớp với ô tiêu đề (vốn đã border-box). Thiếu dòng này thì
            // mỗi ô phình thêm 16px tiền đệm, cả dòng rộng hơn hàng tiêu đề vài trăm
            // pixel và hai bên trượt lệch nhau dần khi cuộn ngang.
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            // Nền phải khớp đúng màu dòng chẵn/lẻ và phủ trọn chiều cao, không thì
            // nội dung các cột trượt qua bên dưới sẽ lộ ra ở mép trên và mép dưới.
            ...(col.fixed === "left" && {
              position: "sticky",
              left: 0,
              alignSelf: "stretch",
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
              zIndex: 9,
              borderRight: "1px solid #E2E8F0",
              boxShadow: "6px 0 6px -4px rgba(15, 23, 42, 0.10)",
            }),
            ...(col.fixed === "right" && {
              position: "sticky",
              right: 0,
              alignSelf: "stretch",
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
              zIndex: 9,
              borderLeft: "1px solid #E2E8F0",
              boxShadow: "-6px 0 6px -4px rgba(15, 23, 42, 0.10)",
            }),
            ...(col.align === "right" && {
              justifyContent: "flex-end",
            }),
            ...(col.align === "left" && {
              justifyContent: "flex-start",
            }),
            ...(col.align === "center" && {
              justifyContent: "center",
            }),
          }}
        >
          {col.render
            ? col.render(record[col.dataIndex], record, index)
            : record[col.dataIndex]}
        </div>
      ))}
    </div>
  );
});

const FeeInputCell = React.memo(({ initialValue, disabled, onCommit }) => {
    const [localVal, setLocalVal] = React.useState(initialValue ?? 0);
    const timerRef = React.useRef(null);

    React.useEffect(() => {
        setLocalVal(initialValue ?? 0);
    }, [initialValue]);

    const commit = (val) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        onCommit(Number(val) || 0);
    };

    const handleChange = (val) => {
        setLocalVal(val ?? 0);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(val), 300); // ✅ debounce 300ms
    };

    React.useEffect(() => {
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, []);

    return (
        <InputNumber
            min={0}
            max={999999999}
            value={localVal}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            placeholder="0"
            controls={false}
            className="w-full collection-amount-input"
            disabled={disabled}
            style={{ width: "100%" }}
            onChange={handleChange}
            onBlur={() => commit(localVal)}   // ✅ click ra ngoài commit ngay
            onPressEnter={() => commit(localVal)} // ✅ bấm Enter commit ngay
        />
    );
});

// Ô nhập tài sản bằng tay (khách chưa có tài sản nào để chọn). Cùng khuôn với
// FeeInputCell: giữ chữ đang gõ ở state nội bộ, chỉ báo ra ngoài khi rời ô — nếu để
// controlled thẳng bằng entryValues thì mỗi ký tự gõ sẽ vẽ lại cả bảng.
const TextCell = React.memo(({ initialValue, disabled, placeholder, onCommit }) => {
    const [localVal, setLocalVal] = React.useState(initialValue ?? "");

    React.useEffect(() => {
        setLocalVal(initialValue ?? "");
    }, [initialValue]);

    return (
        <Input
            value={localVal}
            placeholder={placeholder}
            className="w-full"
            disabled={disabled}
            onChange={(e) => setLocalVal(e.target.value)}
            onBlur={() => onCommit(localVal ?? "")}
            onPressEnter={() => onCommit(localVal ?? "")}
        />
    );
});

// const DiscountCell = React.memo(({ 
//     record, 
//     fees, 
//     entryValues, 
//     discountMode,
//     isEditable,
//     onCommit,
//     onModeChange
// }) => {
//     const eData = entryValues[record.id] || {};
    
//     // ✅ Tính subtotal từ entryValues thay vì form
//     const subtotal = fees.reduce(
//         (sum, fee) => sum + (Number(eData[fee.id]) || 0),
//         0
//     );

//     let autoDiscount = 0;
//     if (record.customer_discount_type === "PERCENTAGE") {
//         autoDiscount = Math.round(
//             subtotal * (Number(record.customer_discount_value) || 0) / 100
//         );
//     } else if (record.customer_discount_type === "FIXED") {
//         autoDiscount = Number(record.customer_discount_value) || 0;
//     }

//     const currentMode = discountMode || "manual";
    
//     // Giá trị hiển thị
//     const displayValue = currentMode === "auto" 
//         ? autoDiscount 
//         : (Number(eData.discount_override) || 0);

//     // Sync autoDiscount vào entryValues khi mode = auto
//     React.useEffect(() => {
//         if (currentMode === "auto") {
//             onCommit(autoDiscount);
//         }
//     }, [currentMode, autoDiscount]);

//     const [localVal, setLocalVal] = React.useState(displayValue);

//     React.useEffect(() => {
//         setLocalVal(displayValue);
//     }, [displayValue]);

//     return (
//         <InputNumber
//             min={0}
//             max={subtotal}
//             value={localVal}
//             disabled={currentMode === "auto" || !isEditable}
//             formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
//             parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
//             placeholder="0"
//             className="w-full"
//             style={{ width: "100%" }}
//             onChange={(val) => setLocalVal(val ?? 0)}
//             onBlur={() => {
//                 if (currentMode === "manual") {
//                     onCommit(Number(localVal) || 0);
//                 }
//             }}
//             addonAfter={
//                 <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 60 }}>
//                     <Switch
//                         checkedChildren="Tự động"
//                         unCheckedChildren="Thủ công"
//                         checked={currentMode === "auto"}
//                         disabled={!isEditable}
//                         onChange={(checked) => {
//                             onModeChange(checked ? "auto" : "manual");
//                             if (checked) {
//                                 onCommit(autoDiscount);
//                                 setLocalVal(autoDiscount);
//                             }
//                         }}
//                     />
//                     <span style={{ fontSize: 12, color: "#999" }}>
//                         {record.customer_discount_type === "PERCENTAGE" ? "%" : "đ"}
//                     </span>
//                 </div>
//             }
//         />
//     );
// });

const DiscountCell = React.memo(({ 
    record, 
    fees, 
    entryValues, 
    discountMode,
    isEditable,
    onCommit,
    onModeChange
}) => {
    const eData = entryValues[record.id] || {};
    
    const subtotal = fees.reduce(
        (sum, fee) => sum + (Number(eData[fee.id]) || 0),
        0
    );

    let autoDiscount = 0;
    if (record.customer_discount_type === "PERCENTAGE") {
        autoDiscount = Math.round(
            subtotal * (Number(record.customer_discount_value) || 0) / 100
        );
    } else if (record.customer_discount_type === "FIXED") {
        autoDiscount = Number(record.customer_discount_value) || 0;
    }

    const currentMode = discountMode || "manual";
    
    const displayValue = currentMode === "auto" 
        ? autoDiscount 
        : (Number(eData.discount_override) || 0);

    const [localVal, setLocalVal] = React.useState(displayValue);
    
    // ✅ Dùng ref để track giá trị đã commit, tránh vòng lặp
    const lastCommittedRef = React.useRef(null);

    
    // React.useEffect(() => {
    //     if (currentMode === "auto" && autoDiscount !== lastCommittedRef.current) {
    //         lastCommittedRef.current = autoDiscount;
    //         onCommit(autoDiscount);
    //         setLocalVal(autoDiscount);
    //     }
    // }, [currentMode, autoDiscount]);

    React.useEffect(() => {
        if (currentMode !== "auto") return;
        
        // ✅ Chỉ commit khi autoDiscount khác với discount_override đang lưu trong entryValues
        const currentStored = Number(entryValues[record.id]?.discount_override) || 0;
        
        if (autoDiscount !== currentStored && autoDiscount !== lastCommittedRef.current) {
            lastCommittedRef.current = autoDiscount;
            onCommit(autoDiscount);
            setLocalVal(autoDiscount);
        }
    }, [currentMode, autoDiscount]);

    // ✅ Khi mode chuyển sang manual, reset ref
    React.useEffect(() => {
        if (currentMode === "manual") {
            lastCommittedRef.current = null;
            setLocalVal(Number(eData.discount_override) || 0);
        }
    }, [currentMode]);

    return (
        <InputNumber
            min={0}
            max={subtotal}
            value={localVal}
            disabled={currentMode === "auto" || !isEditable}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            placeholder="0"
            controls={false}
            className="w-full collection-amount-input"
            style={{ width: "100%" }}
            onChange={(val) => setLocalVal(val ?? 0)}
            onBlur={() => {
                if (currentMode === "manual") {
                    onCommit(Number(localVal) || 0);
                }
            }}
            // addonAfter={
            //     <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 60 }}>
            //         <Switch
            //             checkedChildren="Tự động"
            //             unCheckedChildren="Thủ công"
            //             checked={currentMode === "auto"}
            //             disabled={!isEditable}
            //             onChange={(checked) => {
            //                 onModeChange(checked ? "auto" : "manual");
            //                 if (checked) {
            //                     lastCommittedRef.current = autoDiscount;
            //                     onCommit(autoDiscount);
            //                     setLocalVal(autoDiscount);
            //                 }
            //             }}
            //         />
            //         <span style={{ fontSize: 12, color: "#999" }}>
            //             {record.customer_discount_type === "PERCENTAGE" ? "%" : "đ"}
            //         </span>
            //     </div>
            // }
        />
    );
});

export default function UpdateCollection() {
    // Đếm số lần component vẽ lại. Mốc "React commit" chỉ cho biết tổng thời gian, không
    // cho biết đó là một lần vẽ nặng hay hàng chục lần vẽ chồng nhau — hai cái sửa khác nhau.
    const renderCountRef = React.useRef(0);
    renderCountRef.current += 1;

    const [form] = Form.useForm();
    const isChildEditLocked = Form.useWatch("is_locked", form);
    const [loading, setLoading] = useState(true);
    const [loadingCus, setLoadingCus] = useState(false);
    const [loadingGroup, setLoadingGroup] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Tiến độ job cập nhật đợt thu chạy nền (đợt thu lớn, xem waitForBackgroundUpdate)
    const [bgUpdate, setBgUpdate] = useState({ running: false });
    // ── Step navigation (Wizard UI) ──
    const [currentStep, setCurrentStep] = useState(0);
    const handleNextStep = async () => {
        if (currentStep === 0) {
            try {
                await form.validateFields(['name', 'dateRange', 'collection_type', 'status']);
                setCurrentStep(1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (_) { /* validation fail */ }
        } else if (currentStep === 1) {
            setCurrentStep(2);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    const handlePrevStep = () => {
        setCurrentStep(prev => Math.max(0, prev - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    // ✅ THAY ĐỔI 1: Đổi từ customers → entries
    const [entries, setEntries] = useState([]);
    const [availableCustomers, setAvailableCustomers] = useState([]);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [availableGroups, setAvailableGroups] = useState([]);
    
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    
    const [tempFeeTax, setTempFeeTax] = useState(0);
    const [tempUnit, setTempUnit] = useState('');
    
    const [templateModalVisible, setTemplateModalVisible] = useState(false);
    
    const isSendZns = Form.useWatch("is_send_zns", form);

    const [fees, setFees] = useState([
        { id: "K1", name: "K1", tax: 0, unit: "" },
        { id: "K2", name: "K2", tax: 0, unit: "" },
        { id: "K3", name: "K3", tax: 0, unit: "" }
    ]);

    // Nghiệp vụ hiện chỉ chạy nhóm Dân cư. Hai nhóm kia khoá lại nhưng VẪN GIỮ trong
    // danh sách: đợt thu cũ đã lưu theo Khách hàng/Bãi xe phải hiện đúng loại của nó,
    // bỏ hẳn khỏi danh sách là ô chọn trống trơn, người sửa tưởng dữ liệu bị mất.
    const collectionTypes = [
        { label: "Khách hàng", value: "CUSTOMER", disabled: true },
        { label: "Bãi xe", value: "VEHICLE", disabled: true },
        { label: "Dân cư", value: "APARTMENT" },
    ];

    const [originalData, setOriginalData] = useState(null);

    const [valueOrg, setValueOrg] = useState();
    const [treeData, setTreeData] = useState([]);

    const [filterOrgId, setFilterOrgId] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [sortByOrg, setSortByOrg] = useState(true); // Mặc định bật để show grouped

    // Bảng khoản thu: hàng tiêu đề nằm ngoài vùng cuộn nên phải kéo nó chạy ngang theo
    // thân bảng. Đổi lại, thanh cuộn dọc ở ngay mép phải khung nhìn — không còn phải
    // kéo ngang hết bảng mới với tới được như trước.
    const tableHeaderRef = React.useRef(null);
    const tableBodyRef = React.useRef(null);
    const [tableMaxHeight, setTableMaxHeight] = useState(computeTableHeight);

    // ── Luồng chọn theo Tài sản (Asset-centric) ──
    const ENABLE_ASSET_MODE = true; // Có thể bật/tắt tính năng chọn theo tài sản
    const [selectionMode, setSelectionMode] = useState("CUSTOMER"); // CUSTOMER | ASSET
    const [availableAssets, setAvailableAssets] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(false);

    const fetchAvailableAssets = async (orgId) => {
        try {
            setLoadingAssets(true);
            const response = await assetAPIWithErrorHandling.getAssetAll(orgId);
            const assets = response?.data?.data || [];
            // Chỉ lấy tài sản ĐÃ GÁN chủ sở hữu (customer_id) theo yêu cầu đại ca
            const validAssets = assets.filter(a => a.customer_id && a.customer);
            setAvailableAssets(validAssets);
            if (validAssets.length > 0) {
                message.info(`Tải thành công ${validAssets.length} tài sản đã gán chủ!`);
            }
        } catch (error) {
            message.error("Không thể tải danh sách tài sản: " + error.message);
        } finally {
            setLoadingAssets(false);
        }
    };

    const handleAssetSelect = (assetId) => {
        const asset = availableAssets.find(a => a.id === assetId);
        if (asset && asset.customer) {
            // Tự động map sang chủ sở hữu và gán id tài sản vào form
            addEntry(asset.customer.id, asset.id, asset.customer);
            message.success(`Đã nhận diện chủ sở hữu: ${asset.customer.name}`);
        } else {
            message.warning("Tài sản này chưa được gán chính xác chủ sở hữu!");
        }
    };
    // ──────────────────────────────────────────────

    const [editingFee, setEditingFee] = useState(null);
    const [feeNameModalVisible, setFeeNameModalVisible] = useState(false);
    const [tempFeeName, setTempFeeName] = useState("");
    const [tempFeePrice, setTempFeePrice] = useState(0);
    const [applyPriceToAll, setApplyPriceToAll] = useState(false);

    // const [collectionType, setCollectionType] = useState("CUSTOMER");
    const collectionType = Form.useWatch("collection_type", form);
    const [invoiceCnfs, setInvoiceCnfs] = useState([]);
    const [categories, setCollectionCategories] = useState([]);

    // ✅ THAY ĐỔI 2: discountModes giờ dùng entryId thay vì customerId
    const [discountModes, setDiscountModes] = useState({});

    const [entryValues, setEntryValues] = useState({});

    // ── Import state ──
    const [importFileList, setImportFileList] = useState([]);
    const [importSummaryVisible, setImportSummaryVisible] = useState(false);
    const [importSummary, setImportSummary] = useState({ added: 0, updated: 0, skipped: [] });

    const updateEntryValue = (entryId, field, value) => {
        setEntryValues(prev => ({
            ...prev,
            [entryId]: {
                ...(prev[entryId] || {}),
                [field]: value
            }
        }));
    };

    const [valueSendScheduleAt, setValueSendScheduleAt] = useState(null);
    const onChangeSendScheduleAt = time => {
        setValueSendScheduleAt(time);
    };

    // ✅ THÊM 2 callback này - stable reference, không tạo mới mỗi render
    const handleDiscountCommit = useCallback((entryId, v) => {
        updateEntryValue(entryId, 'discount_override', v);
    }, []); // deps rỗng vì updateEntryValue là stable

    const handleModeChange = useCallback((entryId, newMode) => {
        setDiscountModes(prev => ({
            ...prev,
            [entryId]: newMode
        }));
    }, []);


    const navigate = useNavigate();
    const { id } = useParams();

    const { user } = useSelector(
        (state) => state.persistedReducer.user
    );
    const userType = user?.data.info.type;
    const isStaffScopedUser = userType === "STAFF" || userType === "BRANCH_MANAGER";

    // ✅ Helper function: Generate unique entry ID
    const generateEntryId = () => {
        return `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    useEffect(() => {
        if (id) {
            // Bốn hàm dưới chạy song song với fetchCollectionData nên không nằm trong
            // bảng mốc của nó — đo riêng để biết có hàm nào giữ trang lâu hơn không.
            fetchCollectionData();
            timeAsync("loadOrganizations", loadOrganizations);
            timeAsync("fetchAccount", fetchAccount);
            timeAsync("fetchInvoiceCnf", fetchInvoiceCnf);
            timeAsync("fetchCategories", fetchCategories);
            resumeBackgroundUpdateIfRunning();
        }
    }, [id]);

    useEffect(() => {
        if (valueOrg) {
            timeAsync("fetchGroupCustomer", () => fetchGroupCustomer(valueOrg));
            timeAsync("fetchAvailableCustomers", () => fetchAvailableCustomers(valueOrg, null));
        }
    }, [valueOrg]);
    
    const fetchInvoiceCnf = async () => {
        try {
            const response = await cnfAPIWithErrorHandling.getCnfAll();
            const icfData = response?.data?.data || [];
            setInvoiceCnfs(icfData);
        } catch (error) {
            message.error("Không thể tải danh sách tài khoản xuất hóa đơn: " + error.message);
        }
    };
    
    const fetchCategories = async () => {
        try {
            const response = await collectionCategoryAPIWithErrorHandling.getAllCategories();
            setCollectionCategories(response?.data?.data || []);
        } catch (error) {
            message.error("Không thể tải danh sách loại phí: " + error.message);
        }
    };

    const fetchAccount = async () => {
        try {
            const response = await paymentAccountAPIWithErrorHandling.getAccountAll();
            const groupData = response?.data || [];
            setAccounts(groupData);
            setSelectedAccount(null);
        } catch (error) {
            message.error("Không thể tải danh sách tài khoản: " + error.message);
        }
    };
    const handleSelectAccount = (account) => {
        setSelectedAccount(account);
        message.info(`Đã chọn tài khoản: ${account.name}`);

        // // Cập nhật tài khoản cho các entries phù hợp trong form
        // const currentData = form.getFieldValue("entryData") || {};
        // const newData = { ...currentData };
        // let hasChange = false;

        // entries.forEach(entry => {
        //     if (!newData[entry.id]) {
        //         newData[entry.id] = {
        //             discount_override: 0,
        //             charge_target_number: entry.asset_info || "",
        //         };
        //     }
            
        //     const currentEntryAccId = newData[entry.id].acc_payment_id;
            
        //     if (!currentEntryAccId || currentEntryAccId === oldAccountId) {
        //         if (currentEntryAccId !== account.id) {
        //             newData[entry.id].acc_payment_id = account.id;
        //             hasChange = true;
        //         }
        //     }
        // });

        // if (hasChange) {
        //     form.setFieldValue("entryData", newData);
        let hasChange = false;
        const newEntryValues = { ...entryValues };

        // CHỈ điền cho dòng CHƯA có tài khoản. Trước đây còn đè lên dòng đang mang tài
        // khoản vừa bỏ chọn (`=== oldAccountId`), nên đợt gồm nhiều khối đơn vị là hỏng:
        // chọn TK1 rồi thêm khối A, đổi sang TK2 để thêm khối B thì khối A bị kéo về TK2.
        // Muốn đổi hàng loạt thì dùng nút "Áp cho toàn bộ" / "Áp cho dòng đang lọc".
        entries.forEach(entry => {
            const currentEntryAccId = entryValues[entry.id]?.acc_payment_id;

            if (!currentEntryAccId) {
                newEntryValues[entry.id] = {
                    ...(entryValues[entry.id] || {}),
                    acc_payment_id: account.id
                };
                hasChange = true;
            }
        });

        if (hasChange) {
            setEntryValues(newEntryValues);
            // Xóa đoạn setEntryValues cũ bên dưới vì đã xử lý ở đây


            // // ✅ Sync acc_payment_id vào entryValues
            // setEntryValues(prev => {
            //     const updated = { ...prev };
            //     entries.forEach(entry => {
            //         if (newData[entry.id]?.acc_payment_id) {
            //             updated[entry.id] = {
            //                 ...(updated[entry.id] || {}),
            //                 acc_payment_id: newData[entry.id].acc_payment_id
            //             };
            //         }
            //     });
            //     return updated;
            // });
            
            message.success(`Đã cập nhật tài khoản chung cho các dòng thu chưa chọn riêng`);
        }
    };

    /**
     * Áp tài khoản đang chọn cho một tập dòng — đây là đường DUY NHẤT ghi đè tài khoản
     * của dòng đã có, để việc đổi lựa chọn ở ô trên đầu không âm thầm kéo cả danh sách.
     *
     * @param {string} pham_vi  "tat_ca" | "dang_loc"
     */
    const applyAccountTo = (pham_vi) => {
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản trước");
            return;
        }

        // "dang_loc" bám theo đúng danh sách bảng đang hiển thị (đã trừ dòng tiêu đề nhóm)
        const dsDich =
            pham_vi === "dang_loc"
                ? tableDataSource.filter((item) => !item.isHeader)
                : entries;

        if (dsDich.length === 0) {
            message.info("Không có dòng nào để áp");
            return;
        }

        const canSua = dsDich.filter((entry) => isRowEditable(entry));
        const boQua = dsDich.length - canSua.length;

        if (canSua.length === 0) {
            message.warning("Các dòng này đã phát sinh thanh toán, không đổi được tài khoản");
            return;
        }

        const newEntryValues = { ...entryValues };
        canSua.forEach((entry) => {
            newEntryValues[entry.id] = {
                ...(entryValues[entry.id] || {}),
                acc_payment_id: selectedAccount.id,
            };
        });
        setEntryValues(newEntryValues);

        message.success(
            `Đã áp "${selectedAccount.name}" cho ${canSua.length} dòng` +
                (boQua > 0 ? `, bỏ qua ${boQua} dòng đã phát sinh thanh toán` : "")
        );
    };

    /** Thống kê tiền sẽ về tài khoản nào, để soát trước khi lưu. */
    const accountBreakdown = React.useMemo(() => {
        const dem = new Map();
        entries.forEach((entry) => {
            const accId = entryValues[entry.id]?.acc_payment_id || null;
            dem.set(accId, (dem.get(accId) || 0) + 1);
        });

        return [...dem.entries()].map(([accId, soDong]) => ({
            accId,
            soDong,
            ten: accounts.find((a) => a.id === accId)?.name || "Chưa chọn tài khoản",
        }));
    }, [entries, entryValues, accounts]);

    const handleExportTemplate = async () => {
        if (entries.length === 0) {
            message.warning("Không có dữ liệu để xuất");
            return;
        }

        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Dữ liệu nhập");
            const refSheet = workbook.addWorksheet("Tham chiếu (KHÔNG XÓA)");
            refSheet.state = "hidden";

            // ── Sheet Hướng dẫn ──
            const guideSheet = workbook.addWorksheet("Hướng dẫn");
            const guideData = [
                ["HƯỚNG DẪN SỬ DỤNG FILE NHẬP LIỆU"],
                [""],
                ["1. Các cột định dạng số/chữ xám (như ID Khách hàng, STT) KHÔNG được chỉnh sửa."],
                ["2. Cột 'Tài sản chọn': Click vào ô để hiện mũi tên dropdown, chọn tài sản hoặc nhập mới."],
                ["3. Cột 'Tài khoản nhận': Click vào ô để chọn tài khoản ngân hàng từ danh sách."],
                ["4. Cột khoản thu (K1, K2, K3...): Nhập số tiền, chỉ chấp nhận số nguyên ≥ 0."],
                ["5. Cột 'Giảm trừ': Nhập số tiền giảm trừ cho khách hàng, ≥ 0."],
                [""],
                ["⚠ LƯU Ý QUAN TRỌNG:"],
                ["- KHÔNG xóa hoặc thay đổi Sheet 'Tham chiếu'. File sẽ lỗi nếu Sheet này bị xóa."],
                ["- Cột 'Tài sản chọn' và 'Tài khoản nhận' hiển thị kèm mã ở cuối, dạng: Tên [#12]. Hãy CHỌN TỪ DANH SÁCH, đừng gõ tay, để giữ nguyên phần mã."],
                ["- Cột __record_id đã được tự động ẩn đi. KHÔNG hiện/xóa cột này."],
                ["- Mỗi khách hàng có thể xuất hiện nhiều dòng, nhưng mỗi dòng phải có tài sản KHÁC NHAU."],
                ["- Tối đa 1 dòng trống tài sản cho mỗi khách hàng."],
                ["- Nếu ô bị tô ĐỎ nghĩa là có trùng lặp cặp {Khách hàng - Tài sản}, cần sửa trước khi nhập."],
            ];
            guideData.forEach((row) => guideSheet.addRow(row));
            guideSheet.getRow(1).font = { bold: true, size: 14, color: { argb: "FF1890FF" } };
            guideSheet.getRow(9).font = { bold: true, color: { argb: "FFFF4D4F" } };
            guideSheet.getColumn(1).width = 90;

            // ── 1. Chuẩn bị danh sách tài khoản dùng chung ──
            const accountNames = accounts.map((a) => nhanCoMa(a.name, a.id));
            refSheet.getColumn(1).values = ["Danh sách tài khoản", ...accountNames];
            const accountRange = `'Tham chiếu (KHÔNG XÓA)'!$A$2:$A$${accountNames.length + 1}`;

            // ── 2. Chuẩn bị danh sách tài sản trên ref sheet (cột B trở đi) cho từng khách hàng có >255 ký tự ──
            const assetRefMap = {};
            let refColIndex = 2; // Cột B trở đi
            entries.forEach((entry, index) => {
                const customerAssets = (entry.assets || []).map((a) => nhanCoMa(a.name, a.id));
                if (customerAssets.length > 0) {
                    const assetFormula = `"${customerAssets.join(",")}"`;
                    if (assetFormula.length >= 255) {
                        refSheet.getColumn(refColIndex).values = [
                            `Tài sản KH ${entry.customer_id}`,
                            ...customerAssets,
                        ];
                        assetRefMap[index] = {
                            col: refColIndex,
                            range: `'Tham chiếu (KHÔNG XÓA)'!$${chuCot(refColIndex)}$2:$${chuCot(refColIndex)}$${customerAssets.length + 1}`,
                        };
                        refColIndex++;
                    }
                }
            });

            // ── 0. Thêm metadata row ──
            const collectionNameVal = form.getFieldValue('name') || originalData?.name || 'Chưa đặt tên';
            const dateRangeVal = form.getFieldValue('dateRange');
            const periodStr = dateRangeVal
                ? `${dateRangeVal[0].format('DD/MM/YYYY')} – ${dateRangeVal[1].format('DD/MM/YYYY')}`
                : 'Chưa chọn';
            const metaRow = sheet.addRow([
                `Đợt thu: ${collectionNameVal}`,
                `Kỳ: ${periodStr}`,
                `Xuất ngày: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                `Tổng khách hàng: ${entries.length}`,
            ]);
            metaRow.height = 22;
            metaRow.getCell(1).font = { bold: true, color: { argb: 'FF1890FF' }, size: 11 };
            metaRow.getCell(2).font = { color: { argb: 'FF555555' } };
            metaRow.getCell(3).font = { color: { argb: 'FF555555' } };
            metaRow.getCell(4).font = { bold: true, color: { argb: 'FF52C41A' } };
            sheet.addRow([]); // Hàng trống ngăn cách

            // ── 3. Định nghĩa Header cho sheet chính ──
            const feeColumns = fees.map((f) => f.name);
            const headerRow = [
                "STT",
                "ID Khách hàng (KHÔNG SỬA)",  // 2
                "Tên khách hàng",             // 3
                "Số điện thoại",              // 4
                "Đơn vị",                     // 5
                // Cho người sửa file biết dòng nào không còn chỉnh được. Thiếu cột này thì
                // họ sửa cả dòng đã thu tiền rồi nhập lại, và những dòng đó bị bỏ qua.
                "Trạng thái (KHÔNG SỬA)",     // 6
                // Hai cột ID ẩn cũ đã bỏ: mã giờ nằm luôn trong nhãn, dạng "Tên [#12]",
                // nên chọn lại trong dropdown là mã đi theo, không còn cột nào để lệch.
                "Tài sản chọn (CHỌN TỪ DANH SÁCH)", // 7
                "Tài khoản nhận (CHỌN TỪ DANH SÁCH)", // 8
                ...feeColumns,                // 9+
                "Giảm trừ",                   // 9 + fees
                "__record_id",                // 10 + fees (ẩn)
            ];
            // Vị trí cột (1-based) — dropdown và tô màu bên dưới bám theo các hằng này.
            // Trước đây gõ số thẳng vào getCell() nên chèn thêm một cột là lệch hết.
            const STATUS_COL = 6;
            const ASSET_NAME_COL = 7;
            const ACC_NAME_COL = 8;
            const RECORD_ID_COL = headerRow.length; 
            sheet.addRow(headerRow);

            // Style Header (now row 3 due to metadata + blank row)
            const headerRowNum = 3;

            // Style Header
            const headerRowObj = sheet.getRow(headerRowNum);
            headerRowObj.font = { bold: true, color: { argb: "FF333333" } };
            headerRowObj.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            headerRowObj.height = 30;

            const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
            headerRow.forEach((_, idx) => {
                headerRowObj.getCell(idx + 1).fill = headerFill;
            });

            // ── 4. Đổ dữ liệu từng dòng ──
            const dupMap = new Map();

            entries.forEach((entry, index) => {
                const eId = entry.id;
                const eData = entryValues[eId] || {};
                const org = treeData.find((o) => Number(o.id) === Number(entry.organization_id));

                const currentAssetVal = eData.charge_target_number || entry.charge_target_number || "";
                const selectedAsset = (entry.assets || []).find((a) => String(a.id) === String(currentAssetVal));
                const assetName = selectedAsset ? selectedAsset.name : currentAssetVal;
                const assetId = selectedAsset ? selectedAsset.id : "";

                const account = accounts.find((a) => a.id === eData.acc_payment_id) || selectedAccount;

                const rowValues = [
                    index + 1,
                    entry.customer_id,
                    entry.customer_name,
                    entry.customer_phone,
                    org?.title || "",
                    STATUS_LABELS[entry.status] || "Chưa thanh toán",
                    // Nhãn mang mã: tài sản chưa có trong danh sách (người dùng gõ tay)
                    // thì không có mã, cứ ghi nguyên chữ.
                    selectedAsset ? nhanCoMa(assetName, assetId) : assetName,
                    account ? nhanCoMa(account.name, account.id) : "",
                    ...fees.map((f) => Number(eData[f.id]) || 0),
                    Number(eData.discount_override) || 0,
                    // __record_id (ẩn): ưu tiên id trong CSDL vì nó không đổi giữa các
                    // lần mở màn. entry.id được sinh lại mỗi lần tải trang, nên file xuất
                    // hôm nay mở lại ngày mai sẽ không khớp dòng nào, bị coi là dòng mới
                    // và nhân đôi khách hàng. Dòng chưa lưu thì chưa có db_id, dùng tạm
                    // entry.id — nhập lại ngay trong phiên vẫn khớp.
                    entry.db_id ?? eId,
                ];
                const row = sheet.addRow(rowValues);
                // Data rows start at row 4 (row 1=meta, row 2=blank, row 3=header)
                const rowNum = index + 4;

                // Dòng đã hủy không tô đỏ trùng với dòng thêm lại của nó
                if (entry.status !== "CANCELLED") {
                    const normalizedAsset = String(assetName || "").trim().toLowerCase();
                    const dupKey = `${entry.customer_id}~${normalizedAsset}`;
                    if (!dupMap.has(dupKey)) dupMap.set(dupKey, []);
                    dupMap.get(dupKey).push(rowNum);
                }

                // Đổ màu font mờ cho các cột thông tin (1->5)
                [1, 2, 3, 4, 5, STATUS_COL].forEach((cIdx) => {
                    row.getCell(cIdx).font = { color: { argb: "FF666666" } };
                });

                // --- Dropdown Tài sản cho từng khách hàng ---
                const customerAssets = (entry.assets || []).map((a) => nhanCoMa(a.name, a.id));
                if (customerAssets.length > 0) {
                    if (assetRefMap[index]) {
                        row.getCell(ASSET_NAME_COL).dataValidation = {
                            type: "list",
                            allowBlank: true,
                            formulae: [assetRefMap[index].range],
                            showErrorMessage: false,
                        };
                    } else {
                        const assetFormula = `"${customerAssets.join(",")}"`;
                        row.getCell(ASSET_NAME_COL).dataValidation = {
                            type: "list",
                            allowBlank: true,
                            formulae: [assetFormula],
                            showErrorMessage: false,
                        };
                    }
                }

                // --- Dropdown Tài khoản ngân hàng ---
                if (accountNames.length > 0) {
                    row.getCell(ACC_NAME_COL).dataValidation = {
                        type: "list",
                        allowBlank: true,
                        formulae: [accountRange],
                        showErrorMessage: false,
                    };
                }
            });

            // ── 5. Conditional Formatting lỗi trùng lặp để bôi đỏ ──
            const errorFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFC7CE" } };
            const errorFont = { color: { argb: "FF9C0006" }, bold: true };
            dupMap.forEach((rowNums) => {
                if (rowNums.length > 1) {
                    rowNums.forEach((rn) => {
                        const dupCell = sheet.getRow(rn).getCell(ASSET_NAME_COL); // Cột Tài sản
                        dupCell.fill = errorFill;
                        dupCell.font = errorFont;
                        dupCell.note = `⚠ Trùng lặp! Cùng khách hàng có ${rowNums.length} dòng với cùng tài sản.`;
                    });
                }
            });

            // ── 6. Định dạng độ rộng cột (và ẩn ID thừa) ──
            sheet.columns = [
                { width: 5 },  // STT
                { width: 15 }, // ID Khách hàng
                { width: 25 }, // Tên
                { width: 15 }, // SĐT
                { width: 20 }, // Đơn vị
                { width: 18 }, // Trạng thái
                { width: 42 }, // Tài sản (nhãn kèm mã)
                { width: 42 }, // Tài khoản nhận (nhãn kèm mã)
                ...fees.map(() => ({ width: 15 })),
                { width: 15 }, // Giảm trừ
                { width: 0.1, hidden: true }, // __record_id -> ẩn
            ];

            // ── 7. Sheet Protection ──
            // const dataRowCount = entries.length;
            // for (let r = 2; r <= dataRowCount + 1; r++) {
            //     const row = sheet.getRow(r);
            //     const editableCols = [6, 8];
            //     for (let f = 0; f < fees.length; f++) editableCols.push(10 + f);
            //     editableCols.push(10 + fees.length);
                
            //     editableCols.forEach((colIdx) => {
            //         row.getCell(colIdx).protection = { locked: false };
            //     });
            // }

            // await sheet.protect("onepay2026", {
            //     selectLockedCells: false,
            //     selectUnlockedCells: true,
            //     sort: true,
            //     autoFilter: true,
            //     formatCells: true,
            //     formatColumns: true,
            //     formatRows: true,
            //     insertColumns: true,
            //     insertRows: true,
            //     deleteColumns: true,
            //     deleteRows: true, // Cho phép thêm bớt dòng thỏa mái
            // });

            // ── 8. Xuất file ──
            const buffer = await workbook.xlsx.writeBuffer();
            downloadBlob(
                new Blob([buffer]),
                `Mau_cap_nhat_Dropdown_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
            );
            message.success("Xuất mẫu Excel thành công!");
        } catch (error) {
            console.error("Export template error:", error);
            message.error("Có lỗi khi xuất mẫu Excel: " + error.message);
        }
    };

    // ✅ handleImportExcel: Validate toàn diện, xử lý missing data và chống duplicate
    const handleImportExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                // const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
                const ws = workbook.Sheets[sheetName];

                // ── Auto-detect header row: skip metadata rows ──
                // Thử parse từ row 1 trước (file cũ không có metadata)
                // Nếu không thấy cột "ID Khách hàng (KHÔNG SỬA)", thử từ row 3 (file mới có metadata)
                let rows = XLSX.utils.sheet_to_json(ws);
                // Dòng Excel thật của bản ghi đầu tiên, để mọi thông báo chỉ đúng chỗ cần
                // sửa. File do màn này xuất ra có metadata + dòng trống nên dữ liệu bắt
                // đầu từ dòng 4; báo theo dòng 2 là người dùng dò lệch 2 dòng.
                let firstDataRow = 2;
                if (rows.length > 0 && !Object.prototype.hasOwnProperty.call(rows[0], "ID Khách hàng (KHÔNG SỬA)")) {
                    // Thử parse từ row 3 (range: 2 = bỏ qua 2 rows đầu = metadata + blank)
                    rows = XLSX.utils.sheet_to_json(ws, { range: 2 });
                    firstDataRow = 4;
                }

                if (rows.length === 0) {
                    message.error("File Excel không có dữ liệu");
                    return;
                }

                // ── Bước 1: Pre-validation - Thu thập lỗi ──
                const validationErrors = [];
                const normalizedPairs = new Map();

                rows.forEach((row, idx) => {
                    const excelRow = idx + firstDataRow;
                    const customerId = row["ID Khách hàng (KHÔNG SỬA)"];
                    const customerPhone = String(row["Số điện thoại"] || "").trim();

                    // Dòng đã hủy trong file xuất ra không tính trùng: nó đứng cạnh dòng
                    // thêm lại của cùng khách + tài sản. Lúc nhập, bước dưới bỏ qua nó
                    // như mọi dòng không sửa được.
                    const rawRecordId = row["__record_id"];
                    if (rawRecordId !== undefined && rawRecordId !== null && rawRecordId !== "") {
                        const recorded = entries.find(
                            (e) => e.db_id != null && String(e.db_id) === String(rawRecordId).trim()
                        );
                        if (recorded?.status === "CANCELLED") return;
                    }

                    // Ưu tiên tìm trong entries hiện hữu của Collection (để giữ được db_id)
                    let customer = null;
                    let existingEntry = null;

                    if (customerId) existingEntry = entries.find((e) => Number(e.customer_id) === Number(customerId));
                    if (!existingEntry && customerPhone) existingEntry = entries.find((e) => String(e.customer_phone).trim() === customerPhone);

                    if (existingEntry) {
                        customer = {
                            id: existingEntry.customer_id,
                            name: existingEntry.customer_name,
                            phone: existingEntry.customer_phone,
                            organization_id: existingEntry.organization_id,
                            assets: existingEntry.assets || [],
                        };
                    }

                    // Fallback: Tìm trong danh sách KH available (cho dòng mới chèn)
                    if (!customer) {
                        if (customerId) customer = availableCustomers.find((c) => Number(c.id) === Number(customerId));
                        if (!customer && customerPhone) customer = availableCustomers.find((c) => String(c.phone).trim() === customerPhone);
                    }

                    if (!customer) {
                        validationErrors.push({
                            row: excelRow,
                            column: "ID Khách hàng",
                            message: `Không tìm thấy khách hàng với ID '${customerId || ""}' hoặc SĐT '${customerPhone}'`,
                        });
                        return;
                    }

                    // Normalize tài sản
                    const assetIdFromExcel = row["ID Tài sản (AUTO)"];
                    const assetNameRaw = String(row["Tài sản chọn (CHỌN TỪ DANH SÁCH)"] || "").trim();

                    let assetDisplayName = "";
                    if (assetNameRaw) {
                        const matchedAsset = (customer.assets || []).find((a) => (a.name || "").trim().toLowerCase() === assetNameRaw.toLowerCase());
                        assetDisplayName = matchedAsset ? matchedAsset.name : assetNameRaw;
                    } else if (assetIdFromExcel !== undefined && assetIdFromExcel !== "") {
                        // User xóa trống ô hiển thị Tài sản, coi như cố ý clear tài sản dù cột ẩn có ID (do copy paste Excel sinh ra)
                        assetDisplayName = "";
                    }

                    const normalizedAsset = assetDisplayName.trim().toLowerCase();
                    const dupKey = `${customer.id}~${normalizedAsset}`;

                    if (!normalizedPairs.has(dupKey)) {
                        normalizedPairs.set(dupKey, { rows: [], customerName: customer.name, asset: assetDisplayName });
                    }
                    normalizedPairs.get(dupKey).rows.push(excelRow);
                });

                // Check duplicate pairs
                normalizedPairs.forEach((info, key) => {
                    if (info.rows.length > 1) {
                        const isEmptyAsset = key.endsWith("~");
                        if (isEmptyAsset && info.rows.length === 2) {
                            validationErrors.push({
                                row: info.rows[1],
                                column: "Tài sản",
                                message: `Khách hàng '${info.customerName}' đã có 1 dòng không có tài sản (dòng ${info.rows[0]}). Tối đa 1 dòng trống tài sản.`,
                            });
                        } else if (isEmptyAsset && info.rows.length > 2) {
                            info.rows.slice(1).forEach((rn) => {
                                validationErrors.push({
                                    row: rn,
                                    column: "Tài sản",
                                    message: `Khách hàng '${info.customerName}' có ${info.rows.length} dòng trống tài sản (dòng ${info.rows.join(", ")}). Tối đa 1 dòng.`,
                                });
                            });
                        } else if (!isEmptyAsset) {
                            info.rows.slice(1).forEach((rn) => {
                                validationErrors.push({
                                    row: rn,
                                    column: "Tài sản",
                                    message: `Tài sản '${info.asset}' bị trùng với dòng ${info.rows[0]} của cùng khách hàng '${info.customerName}'`,
                                });
                            });
                        }
                    }
                });

                // Validate số tiền
                rows.forEach((row, idx) => {
                    const excelRow = idx + firstDataRow;
                    fees.forEach((fee) => {
                        const val = row[fee.name];
                        if (val !== undefined && val !== "") {
                            const num = Number(val);
                            if (isNaN(num) || num < 0) {
                                validationErrors.push({
                                    row: excelRow,
                                    column: fee.name,
                                    message: `Giá trị '${val}' không hợp lệ. Phải là số ≥ 0.`,
                                });
                            }
                        }
                    });
                    const discountVal = row["Giảm trừ"];
                    if (discountVal !== undefined && discountVal !== "") {
                        const num = Number(discountVal);
                        if (isNaN(num) || num < 0) {
                            validationErrors.push({
                                row: excelRow,
                                column: "Giảm trừ",
                                message: `Giá trị giảm trừ '${discountVal}' không hợp lệ. Phải là số ≥ 0.`,
                            });
                        }
                    }
                });

                if (validationErrors.length > 0) {
                    validationErrors.sort((a, b) => a.row - b.row);
                    Modal.error({
                        title: `Phát hiện ${validationErrors.length} lỗi dữ liệu`,
                        width: 700,
                        content: (
                            <div style={{ maxHeight: 400, overflowY: "auto" }}>
                                <Alert
                                    message="Vui lòng sửa các lỗi sau trong file Excel rồi nhập lại."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                />
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                                            <th style={{ padding: "8px", textAlign: "left", width: 60 }}>Dòng</th>
                                            <th style={{ padding: "8px", textAlign: "left", width: 120 }}>Cột</th>
                                            <th style={{ padding: "8px", textAlign: "left" }}>Lý do lỗi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validationErrors.map((err, i) => (
                                            <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                                                <td style={{ padding: "6px 8px", color: "#E5065C", fontWeight: 600 }}>{err.row}</td>
                                                <td style={{ padding: "6px 8px", fontWeight: 500 }}>{err.column}</td>
                                                <td style={{ padding: "6px 8px" }}>{err.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ),
                    });
                    return;
                }

                // ── Bước 2: Apply data ──
                const newEntries = [...entries];
                const newEntryValues = { ...entryValues };
                const newDiscountModes = { ...discountModes };
                let addedCount = 0;
                let updatedCount = 0;
                // Dòng khớp với khoản thu đã phát sinh thanh toán / đã hủy: không ghi đè.
                // Backend cũng bỏ qua chúng khi lưu, nên nếu ở đây vẫn đổi thì màn hình sẽ
                // hiện số mới trong khi CSDL giữ số cũ — đúng kiểu sai mà không ai biết.
                const skippedRows = [];
                // Đếm dòng mà cột tên và cột ID ẩn đá nhau — hệ thống nghe theo tên, và
                // phải nói cho người nhập biết là nó đã hiểu đúng ý họ.
                let accChangedByName = 0;
                let accNameNotFound = 0;
                // Đếm dòng chọn tài sản thuộc đơn vị KHÁC đơn vị của đợt thu. Danh sách
                // tài sản trong file là toàn bộ tài sản của khách, không lọc theo đơn vị,
                // nên khách có căn ở nhiều khối rất dễ chọn nhầm sang khối khác — mà cột
                // "Đơn vị" trong file chỉ để xem, nhập lại không đổi được đơn vị của dòng.
                const assetKhacDonVi = [];
                const usedRecordIds = new Set(); // Theo dõi ID đã dùng (chống copy paste trùng __record_id trong Excel)

                const CHUNK_SIZE = 100;
                const processChunk = (startIdx) => {
                    const endIdx = Math.min(startIdx + CHUNK_SIZE, rows.length);

                    for (let i = startIdx; i < endIdx; i++) {
                        const row = rows[i];
                        const customerId = row["ID Khách hàng (KHÔNG SỬA)"];
                        const customerPhone = String(row["Số điện thoại"] || "").trim();
                        // Excel trả về số cho db_id và chuỗi cho entry.id, nên so sánh
                        // bằng chuỗi cho mọi trường hợp.
                        const rawRecordId = row["__record_id"];
                        let recordId =
                            rawRecordId === undefined || rawRecordId === null || rawRecordId === ""
                                ? null
                                : String(rawRecordId).trim();

                        // Nếu recordId đã được dùng ở dòng trước (do user copy-paste ở Excel) -> coi như dòng mới
                        if (recordId) {
                            if (usedRecordIds.has(recordId)) {
                                recordId = null;
                            } else {
                                usedRecordIds.add(recordId);
                            }
                        }

                        let customer = null;
                        let existingEntry = null;

                        if (customerId) existingEntry = newEntries.find((e) => Number(e.customer_id) === Number(customerId));
                        if (!existingEntry && customerPhone) existingEntry = newEntries.find((e) => String(e.customer_phone).trim() === customerPhone);

                        if (existingEntry) {
                            customer = {
                                id: existingEntry.customer_id,
                                name: existingEntry.customer_name,
                                phone: existingEntry.customer_phone,
                                organization_id: existingEntry.organization_id,
                                assets: existingEntry.assets || [],
                            };
                        }

                        if (!customer) {
                            if (customerId) customer = availableCustomers.find((c) => Number(c.id) === Number(customerId));
                            if (!customer && customerPhone) customer = availableCustomers.find((c) => String(c.phone).trim() === customerPhone);
                        }
                        if (!customer) continue;

                        const assetIdFromExcel = row["ID Tài sản (AUTO)"];
                        const assetNameFromExcel = String(row["Tài sản chọn (CHỌN TỪ DANH SÁCH)"] || "").trim();

                        // Nhãn dạng "Tên [#12]": mã nằm ngay trong ô nên chọn lại trong
                        // dropdown là mã đi theo — không còn cột ẩn nào để lệch.
                        const assetNhan = bocMa(assetNameFromExcel);

                        let assetVal = "";
                        if (assetNhan.id) {
                            const theoMa = (customer.assets || []).find((a) => String(a.id) === String(assetNhan.id));
                            assetVal = theoMa ? theoMa.id : assetNhan.ten;
                        } else if (assetNameFromExcel) {
                            // File cũ (nhãn chưa mang mã) hoặc người dùng gõ tay: giữ nguyên
                            // đường dò cũ theo cột ID ẩn rồi tới tên.
                            const normalizedInput = assetNhan.ten.toLowerCase();
                            const customerAssets = customer.assets || [];

                            // Cột ID ẩn chỉ được tin khi TÊN trong ô còn khớp với tài sản
                            // mang id đó, và tài sản đó đúng là của khách ở dòng này.
                            // Dropdown trong Excel chỉ đổi tên, ô ID vẫn giữ giá trị cũ —
                            // tin ID vô điều kiện là nuốt mất thao tác đổi tài sản.
                            const assetById =
                                assetIdFromExcel !== undefined && assetIdFromExcel !== ""
                                    ? customerAssets.find((a) => String(a.id) === String(assetIdFromExcel))
                                    : null;

                            if (assetById && (assetById.name || "").trim().toLowerCase() === normalizedInput) {
                                // Khớp cả id lẫn tên thì lấy thẳng id, khỏi dò theo tên và
                                // khỏi vớ nhầm tài sản trùng tên của cùng khách.
                                assetVal = assetById.id;
                            } else {
                                const matchedAsset = customerAssets.find((a) => (a.name || "").trim().toLowerCase() === normalizedInput);
                                assetVal = matchedAsset ? matchedAsset.id : assetNameFromExcel;
                            }
                        } else if (assetIdFromExcel !== undefined && assetIdFromExcel !== "") {
                            // User xóa trống ô hiển thị Tài sản, coi như cố ý clear tài sản (bỏ qua cột ẩn copy dư)
                            assetVal = "";
                        }

                        const accountIdFromExcel = row["ID Tài khoản (AUTO)"];
                        const accountNameFromExcel = row["Tài khoản nhận (CHỌN TỪ DANH SÁCH)"];

                        // Ưu tiên Upsert: Nếu trùng id, lấy đúng cái cũ (để bảo toàn db_id đợt thu API cập nhật)
                        let entry = null;

                        if (recordId) {
                            // Dò theo id trong CSDL trước — đây là thứ file mang theo và
                            // là thứ duy nhất không đổi giữa các lần mở màn. Chỉ khi không
                            // có mới dò theo entry.id, để file xuất trong cùng phiên (dòng
                            // chưa lưu) vẫn khớp.
                            entry =
                                newEntries.find(
                                    (e) => e.db_id != null && String(e.db_id) === recordId
                                ) || newEntries.find((e) => String(e.id) === recordId);

                            // Dòng tìm được phải đúng khách của dòng đang đọc. __record_id
                            // là cột ẩn: người dùng sao chép một dòng để tạo dòng cho khách
                            // khác, sửa tên/SĐT/ID khách nhưng không thấy mà xoá cột này —
                            // thiếu chốt ở đây là tiền của khách mới ghi đè vào dòng khách cũ.
                            if (entry && String(entry.customer_id) !== String(customer.id)) {
                                entry = null;
                            }
                        }

                        if (!entry) {
                            const sameRow = (e) =>
                                String(e.customer_id) === String(customer.id) &&
                                (String(e.asset_info || "") === String(assetVal || "") || String(e.charge_target_number || "") === String(assetVal || "") || String(e.asset_id || "") === String(assetVal || ""));
                            // Cùng khách + tài sản có thể có cả dòng đã hủy lẫn dòng thêm lại:
                            // ưu tiên dòng còn hiệu lực, kẻo tiền trong file bị bỏ qua vì
                            // khớp nhầm vào dòng đã hủy.
                            entry =
                                newEntries.find((e) => sameRow(e) && e.status !== "CANCELLED") ||
                                newEntries.find(sameRow);
                        }

                        // Dòng đã có trong đợt thu mà không còn sửa được thì giữ nguyên.
                        // Đặt trước nhánh tạo mới để không sinh thêm dòng trùng cho cùng
                        // khách hàng — khoản thu đã thu tiền mà nhân đôi là hỏng đối soát.
                        if (entry && !isRowEditable(entry)) {
                            skippedRows.push({
                                row: i + firstDataRow,
                                customer: entry.customer_name || customer.name,
                                reason: rowLockReason(entry),
                            });
                            continue;
                        }

                        // Nếu là entry mới thêm
                        if (!entry) {
                            const entryId = generateEntryId();
                            entry = {
                                id: entryId,
                                // db_id bị khuyết, đây là keyword để API gọi Create thay vì Update
                                customer_id: customer.id,
                                customer_name: customer.name,
                                customer_phone: customer.phone,
                                customer_discount_type: customer.discount_type ?? null,
                                customer_discount_value: customer.discount_value ?? 0,
                                organization_id: customer.organization_id || valueOrg,
                                charge_target_number: assetVal,
                                asset_info: assetVal,
                                assets: customer.assets || [],
                            };
                            newEntries.push(entry);
                            newDiscountModes[entryId] = "manual";
                            addedCount++;
                        } else {
                            updatedCount++;
                        }

                        const entryId = entry.id;
                        const eData = { ...(newEntryValues[entryId] || {}) };
                        eData.charge_target_number = assetVal;
                        eData.customer_id = customer.id;

                        fees.forEach((fee) => {
                            if (row[fee.name] !== undefined) {
                                eData[fee.id] = Number(row[fee.name]) || 0;
                            }
                        });

                        if (row["Giảm trừ"] !== undefined) {
                            eData.discount_override = Number(row["Giảm trừ"]) || 0;
                            newDiscountModes[entryId] = "manual";
                        }

                        // Cột ID ẩn chỉ được tin khi TÊN trong ô còn khớp với tài khoản
                        // mang id đó. Dropdown trong Excel chỉ đổi cột tên, cột ID bị ẩn
                        // nên giữ nguyên giá trị cũ — tin ID vô điều kiện là nuốt mất
                        // thao tác đổi tài khoản của người dùng.
                        // Nhãn dạng "Tên [#12]" — ưu tiên mã nằm trong chính ô
                        const accNhan = bocMa(accountNameFromExcel);
                        const accTheoMa = accNhan.id
                            ? accounts.find((a) => String(a.id) === String(accNhan.id))
                            : null;

                        const accNameKey = accNhan.ten.toLowerCase();
                        const accById =
                            accountIdFromExcel !== undefined && accountIdFromExcel !== ""
                                ? accounts.find((a) => String(a.id) === String(accountIdFromExcel))
                                : null;

                        if (accTheoMa) {
                            // Mã đi kèm ngay trong nhãn: tin tuyệt đối
                            eData.acc_payment_id = accTheoMa.id;
                        } else if (accById && (accById.name || "").trim().toLowerCase() === accNameKey) {
                            // File cũ: cột tên và cột ID ẩn còn khớp nhau
                            eData.acc_payment_id = accById.id;
                        } else if (accNameKey) {
                            // Hai cột đá nhau, hoặc ô ID trống: người dùng vừa đổi tay nên
                            // nghe theo TÊN
                            const matchedAcc = accounts.find((a) => (a.name || "").trim().toLowerCase() === accNameKey);
                            if (matchedAcc) {
                                if (accById && matchedAcc.id !== accById.id) accChangedByName++;
                                eData.acc_payment_id = matchedAcc.id;
                            } else {
                                // Gõ/dán tên không có trong danh sách: giữ tài khoản cũ cho
                                // dòng vẫn sinh được QR, nhưng phải đếm để báo — im lặng giữ
                                // nguyên là người nhập tưởng đã đổi được.
                                accNameNotFound++;
                                if (accById) eData.acc_payment_id = accById.id;
                            }
                        } else if (accById) {
                            // Ô tên bị xoá trắng mà ID còn: giữ nguyên tài khoản cũ, vì
                            // dòng nào cũng phải có tài khoản mới sinh được mã QR
                            eData.acc_payment_id = accById.id;
                        }

                        // Soát đơn vị của tài sản so với đơn vị của dòng. Chỉ soát khi
                        // biết chắc cả hai — tài sản gõ tay (chưa có trong danh sách) hoặc
                        // dữ liệu cũ thiếu organization_id thì bỏ qua, không đoán.
                        const assetDaChon = (customer.assets || []).find(
                            (a) => String(a.id) === String(assetVal)
                        );
                        if (
                            assetDaChon?.organization_id &&
                            entry.organization_id &&
                            String(assetDaChon.organization_id) !== String(entry.organization_id)
                        ) {
                            assetKhacDonVi.push({
                                row: i + firstDataRow,
                                customer: entry.customer_name || customer.name,
                                asset: assetDaChon.name,
                                orgAsset:
                                    treeData.find((o) => Number(o.id) === Number(assetDaChon.organization_id))?.title
                                    || `đơn vị #${assetDaChon.organization_id}`,
                            });
                        }

                        newEntryValues[entryId] = eData;
                    }

                    if (endIdx < rows.length) {
                        setTimeout(() => processChunk(endIdx), 0);
                    } else {
                        setEntries(newEntries);
                        setEntryValues(newEntryValues);
                        setDiscountModes(newDiscountModes);
                        // Reset upload file để có thể nhập lại cùng file
                        setImportFileList([]);
                        // Hiện modal tóm tắt thay vì message đơn giản
                        setImportSummary({ added: addedCount, updated: updatedCount, skipped: skippedRows });
                        setImportSummaryVisible(true);

                        // Nói rõ khi file có dòng sửa tên tài khoản: người nhập cần biết
                        // hệ thống đã nghe theo tên chứ không giữ tài khoản cũ.
                        if (accChangedByName > 0) {
                            message.info(`Đã đổi tài khoản nhận theo tên trong file cho ${accChangedByName} dòng`);
                        }
                        if (accNameNotFound > 0) {
                            message.warning(
                                `${accNameNotFound} dòng ghi tên tài khoản không có trong danh sách, đã giữ nguyên tài khoản cũ`
                            );
                        }
                        if (assetKhacDonVi.length > 0) {
                            // Không chặn, chỉ nói rõ: có trường hợp thu hộ căn thuộc khối
                            // khác là hợp lệ, nhưng không được để lệch mà không ai biết.
                            Modal.warning({
                                title: `${assetKhacDonVi.length} dòng có tài sản thuộc đơn vị khác`,
                                width: 640,
                                content: (
                                    <div>
                                        <div style={{ marginBottom: 8 }}>
                                            Đơn vị của dòng thu vẫn giữ theo đợt thu. Kiểm lại nếu đây không phải ý bạn:
                                        </div>
                                        <div style={{ maxHeight: 260, overflowY: "auto", fontSize: 13 }}>
                                            {assetKhacDonVi.slice(0, 50).map((item, i) => (
                                                <div key={i} style={{ padding: "3px 0", borderTop: i === 0 ? "none" : "1px dashed #F1F5F9" }}>
                                                    <b>Dòng {item.row}</b> — {item.customer}: tài sản{" "}
                                                    <b>{item.asset}</b> thuộc <b>{item.orgAsset}</b>
                                                </div>
                                            ))}
                                            {assetKhacDonVi.length > 50 && (
                                                <div style={{ marginTop: 6, color: "#64748B" }}>
                                                    … và {assetKhacDonVi.length - 50} dòng nữa
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ),
                            });
                        }
                    }
                };

                if (rows.length > CHUNK_SIZE) {
                    message.loading({ content: `Đang xử lý ${rows.length} dòng dữ liệu...`, key: "importProgress", duration: 0 });
                    processChunk(0);
                    setTimeout(() => {
                        message.destroy("importProgress");
                    }, Math.ceil(rows.length / CHUNK_SIZE) * 50 + 500);
                } else {
                    processChunk(0);
                }
            } catch (error) {
                console.error("Import error:", error);
                message.error("Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file mẫu.");
            }
        };
        reader.readAsBinaryString(file);
        return false;
    };

    const fetchGroupCustomer = async (id) => {
        try {
            setLoadingGroup(true);
            const response = await groupCustomerAPIWithErrorHandling.getGroupAllByOrg(id);
            const groupData = response?.data || [];
            setAvailableGroups(groupData);
            setSelectedGroup(null);
        } catch (error) {
            message.error("Không thể tải danh sách nhóm khách hàng: " + error.message);
        } finally {
            setLoadingGroup(false);
        }
    };

    const fetchCollectionData = async () => {
        // Đo từng bước để biết màn này chậm ở đâu. Bật bằng localStorage "onepay.perf" = "1".
        const trace = createTrace("Mở màn Sửa đợt thu");
        const renderAtStart = renderCountRef.current;
        try {
            setLoading(true);
            const response = await collectionAPIWithErrorHandling.getCollectionById(id);
            const collection = response.data?.data;
            trace.mark("API getCollectionById (tải cả đợt)");

            if (!collection) {
                message.error("Không tìm thấy đợt thu!");
                navigate("/collections");
                return;
            }

            const isLockedCollection = collection?.is_locked === true || collection?.is_locked === 1;
            const isChildCollection = collection?.type_child === "CHILD";

            // if (isStaffScopedUser && (!isChildCollection || isLockedCollection)) {
            if (isStaffScopedUser && isChildCollection && isLockedCollection) {

                message.error(
                    isChildCollection
                        ? "Tài khoản cấp con đang bị khóa chỉnh sửa đợt thu này!"
                        : "Bạn chỉ được chỉnh sửa đợt thu của đơn vị mình khi tài khoản cha cho phép."
                );
                navigate(`/collections/information/${id}`);
                return;
            }

            setOriginalData(collection);

            let collectionCusFees = [];

            if (collection.type_child === 'PARENT' && collection.children?.length > 0) {
                collection.children.forEach(child => {
                    collectionCusFees = collectionCusFees.concat(child.customer_fees || []);
                });
            } else {
                collectionCusFees = collection.customer_fees || [];
            }
            trace.mark("Gộp customer_fees", `${collectionCusFees.length} dòng`);

            // const feeDefinitions = collection.fee_definitions || [];
            // if (feeDefinitions.length > 0) {
            //     setFees(feeDefinitions);
            // } else {
            //     const allFeeTypes = new Set();
            //     collectionCusFees?.forEach(customerFee => {
            //         Object.keys(customerFee.fees || {}).forEach(feeType => {
            //             allFeeTypes.add(feeType);
            //         });
            //     });

            //     if (allFeeTypes.size > 0) {
            //         const extractedFees = Array.from(allFeeTypes).map(feeId => ({
            //             id: feeId,
            //             name: feeId,
            //             tax: 0,        // ✅ THÊM
            //             unit: "",      // ✅ THÊM
            //             price: 0       // ✅ THÊM
            //         }));
            //         setFees(extractedFees);
            //     }
            // }
            const feeDefinitions = collection.fee_definitions || [];
            let resolvedFees = [
                { id: "K1", name: "K1", tax: 0, unit: "" },
                { id: "K2", name: "K2", tax: 0, unit: "" },
                { id: "K3", name: "K3", tax: 0, unit: "" }
            ];

            if (feeDefinitions.length > 0) {
                resolvedFees = feeDefinitions;
                setFees(feeDefinitions);
            } else {
                const allFeeTypes = new Set();
                collectionCusFees?.forEach(customerFee => {
                    Object.keys(customerFee.fees || {}).forEach(feeType => {
                        allFeeTypes.add(feeType);
                    });
                });

                if (allFeeTypes.size > 0) {
                    resolvedFees = Array.from(allFeeTypes).map(feeId => ({
                        id: feeId,
                        name: feeId,
                        tax: 0,
                        unit: "",
                        price: 0
                    }));
                    setFees(resolvedFees);
                }
            }

            // ✅ THAY ĐỔI 3: Convert customer_fees thành entries
            const entriesData = collectionCusFees?.map(cf => {
                const entryId = generateEntryId();
                return {
                    id: entryId, // Unique entry ID
                    db_id: cf.id, // ID từ database (để update)
                    customer_id: cf.customer_id,
                    customer_name: cf.customer?.name || `Khách hàng ${cf.customer_id}`,
                    customer_phone: cf.customer?.phone || "",
                    customer_discount_type: cf.customer?.discount_type || null,
                    customer_discount_value: cf.customer?.discount_value || 0,
                    organization_id: cf.collection.organization_id,
                    charge_target_number: cf.charge_target_number || "",
                    asset_info: cf.charge_target_number || "", // Alias cho dễ hiểu
                    fees: cf.fees || {},
                    acc_payment_id: cf.acc_payment_id || "",
                    assets: cf.customer?.assets || [],
                    asset_id: cf.asset_id || null,
                    asset_name: cf.asset?.name || null,
                    status: cf.status || "UNPAID",
                    amount_paid: Number(cf.amount_paid || 0),
                };
            }) || [];
            
            trace.mark("Dựng entriesData", `${entriesData.length} dòng`);

            // setEntries(entriesData);
            const uniqueOrgIds = [...new Set(entriesData.map(e => e.organization_id).filter(Boolean))];

            // Gọi GỘP 1 request cho tất cả đơn vị. Bản cũ await tuần tự trong vòng lặp:
            // phường ~40 khu phố = 40 vòng đi-về + 40 preflight CORS, đo thực tế >80s mới
            // mở được trang. Bản gộp cũng loại trùng nên dropdown chọn khách hàng không
            // còn hiện lặp khi 1 cư dân thuộc nhiều đơn vị.
            let allCustomers = [];
            if (uniqueOrgIds.length > 0) {
                try {
                    // organization.js's withErrorHandling tra ve envelope {success, data, message}
                    // nen du lieu nam o res.data (khac customer.js tra ve nguyen axios response)
                    const res = await organizationAPIWithErrorHandling.getCustomerAllByOrgs(uniqueOrgIds);
                    allCustomers = res.data || [];
                } catch (e) {
                    allCustomers = [];
                }
            }
            trace.mark(
                "API getCustomerAllByOrgs",
                `${uniqueOrgIds.length} đơn vị → ${allCustomers.length} khách`
            );

            const enrichedEntries = entriesData.map(entry => {
                const matched = allCustomers.find(c => c.id === entry.customer_id);
                return {
                    ...entry,
                    assets: giuTaiSanCuaKhoanThu(matched?.assets || entry.assets || [], entry)
                };
            });
            trace.mark(
                "Ghép tài sản vào entries (find lồng map)",
                `${entriesData.length} × ${allCustomers.length} phép so sánh`
            );

            setEntries(enrichedEntries);
            setAvailableCustomers(allCustomers);

            // ✅ THAY ĐỔI 4: discountModes dùng entryId
            const initialDiscountModes = {};
            entriesData.forEach(entry => {
                initialDiscountModes[entry.id] = 'manual';
            });
            setDiscountModes(initialDiscountModes);
            trace.mark("Dựng discountModes");

            // ✅ THAY ĐỔI 5: Form data dùng entryId
            // const entryData = {};
            // entriesData.forEach(entry => {
            //     entryData[entry.id] = {
            //         ...entry.fees,
            //         discount_override: Number(collectionCusFees.find(cf => cf.customer_id === entry.customer_id && cf.charge_target_number === entry.charge_target_number)?.discount_override) || 0,
            //         charge_target_number: entry.asset_id || entry.charge_target_number || "",
            //         acc_payment_id: entry.acc_payment_id || ""
            //     };
            // });

            const entryData = {};
            entriesData.forEach(entry => {
                // const matchedCf = collectionCusFees.find(
                //     cf => cf.customer_id === entry.customer_id && 
                //     cf.charge_target_number === entry.charge_target_number
                // );

                const matchedCf = collectionCusFees.find(
                    cf => cf.customer_id === entry.customer_id && 
                    (cf.asset_id || null) === (entry.asset_id || null) &&
                    (cf.charge_target_number || "") === (entry.charge_target_number || "")
                );

                entryData[entry.id] = {
                    discount_override: Number(matchedCf?.discount_override) || 0,
                    charge_target_number: entry.asset_id || entry.charge_target_number || "",
                    acc_payment_id: entry.acc_payment_id || ""
                };

                // Dùng resolvedFees (biến local) thay vì fees (state chưa update kịp)
                resolvedFees.forEach(fee => {
                    entryData[entry.id][fee.id] = Number(entry.fees?.[fee.id]) || 0;
                });
            });
            trace.mark(
                "Dựng entryData (find lồng forEach)",
                `${entriesData.length} × ${collectionCusFees.length} phép so sánh`
            );

            const sendSchedule = collection.send_schedule_at ? dayjs(collection.send_schedule_at, "HH:mm:ss") : null;
            setValueSendScheduleAt(sendSchedule);

            // setCollectionType(collection.charge_target_type);
            setEntryValues(entryData);

            form.setFieldsValue({
                name: collection.name,
                description: collection.description,
                dateRange: [
                    dayjs(collection.start_date),
                    dayjs(collection.end_date)
                ],
                status: collection.status,
                is_send_zns: collection.is_send_zns,
                is_locked: collection.is_locked === true || collection.is_locked === 1,
                send_schedule_at: collection.send_schedule_at ? dayjs(collection.send_schedule_at, "HH:mm:ss") : null,
                send_date: collection.send_date ? dayjs(collection.send_date) : null,
                before_date: collection.before_date ?? null,
                after_date: collection.after_date ?? null,
                collection_type: collection.charge_target_type,
                // invoice_cnf_id: collection.invoice_cnf_id ?? null,
                collection_category_id: collection.collection_category_id ?? null,
                transfer_content: collection.transfer_content ?? null,
            });
            trace.mark("setFieldsValue — thông tin đợt", "khoảng 12 field");

            // Form chỉ còn giữ 12 field thông tin đợt. Dữ liệu từng dòng thu nằm ở state
            // entryValues — nguồn duy nhất, cũng là thứ handleSubmit đọc khi gửi đi.
            //
            // Trước đây có thêm form.setFieldsValue({ entryData }) ở đây. Đo 15/09/2026 trên
            // đợt cha 6.903 dòng: riêng dòng đó tốn 31,6 giây và kéo thêm 36,8 giây ở lần vẽ
            // kế tiếp — 68,7 trong tổng 76,3 giây mở màn, chỉ để phục vụ 2 ô nhập mà nay đã
            // đọc thẳng entryValues. Đừng đưa lại vào.

            // ── Bỏ tự động qua Bước 2 để người dùng có thể thấy và sửa thông tin gốc ở Bước 1 ──
            // if (entriesData && entriesData.length > 0) {
            //     setCurrentStep(1);
            // }

            // Tắt loading TRƯỚC khi đo mốc vẽ: đây mới là lần render thật sự đưa bảng ra
            // màn hình, phần trên mới chỉ là tính toán trong bộ nhớ.
            setLoading(false);
            await trace.markPaint(
                "React commit + trình duyệt vẽ",
                `${renderCountRef.current - renderAtStart} lần vẽ lại`
            );

        } catch (error) {
            console.error("fetchCollectionData lỗi:", error);
            message.error("Không thể tải thông tin đợt thu: " + error.message);
            navigate("/collections");
        } finally {
            setLoading(false);
            // In bảng đo ở đây để còn thấy được các mốc đã chạy khi hàm hỏng giữa chừng.
            trace.end();
        }
    };

    const fetchAvailableCustomers = async (id, groupId = null) => {
        try {
          setLoadingCus(true);
          let customersData = [];
          
          if (groupId) {
            const response = await groupCustomerAPIWithErrorHandling.getCustomerAllByGroup(groupId);
            customersData = response.data || [];
            message.info(`Tải thành công ${customersData.length} khách hàng thuộc nhóm!`);
          } else {
            const response = await organizationAPIWithErrorHandling.getCustomerAllByOrg(id);
            customersData = response.data || [];
            message.info(`Tải thành công ${customersData.length} khách hàng thuộc đơn vị!`);
          }
          
          setAvailableCustomers(customersData);
        } catch (error) {
          message.error("Không thể tải danh sách khách hàng: " + error.message);
        } finally {
          setLoadingCus(false);
        }
    };
    
    const onChange = (newValOrg) => {
        setValueOrg(newValOrg);
        setAvailableCustomers([]);
        setAvailableAssets([]);
        setSelectedGroup(null);
        if (newValOrg && selectionMode === "ASSET") {
            fetchAvailableAssets(newValOrg);
        }
    };

    const handleModeChangeSelection = (e) => {
        const mode = e.target.value;
        setSelectionMode(mode);
        if (mode === "ASSET" && valueOrg && availableAssets.length === 0) {
            fetchAvailableAssets(valueOrg);
        }
    };

    const handleGroupChange = (groupId) => {
        setSelectedGroup(groupId);
        if (groupId) {
            fetchAvailableCustomers(valueOrg, groupId);
        } else {
            fetchAvailableCustomers(valueOrg, null);
        }
    };

    const loadOrganizations = async () => {
        try {
            const response = await organizationAPIWithErrorHandling.getTreeStructure();
            if (response.success) {
                const rawData = response.data;
                const flatTree = flattenTreeData(rawData);
                setTreeData(flatTree); // Dùng cho TreeSelect
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    const flattenTreeData = (nodes, parentId = 0) => {
        let result = [];
        nodes.forEach((node) => {
            result.push({
                id: node.id,
                pId: parentId,
                value: String(node.id),
                title: node.name,
                isLeaf: node.children_recursive?.length === 0,
            });

            if (node.children_recursive && node.children_recursive.length > 0) {
                result = result.concat(
                    flattenTreeData(node.children_recursive, node.id)
                );
            }
        });
        return result;
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            const startDate = dates[0];
            const month = startDate.month() + 1;
            const year = startDate.year();
            const suggestedName = collectionHelpers.generateCollectionName(month, year);
            const checkName = form.getFieldValue("name");
            if(!checkName){
                form.setFieldValue("name", suggestedName);
            }
        }
    };

    /* ================== ĐOẠN CODE CŨ (ĐÃ YÊU CẦU COMMENT LẠI) ==================
    // ✅ THAY ĐỔI 6: addEntry thay vì addCustomer (OLD)
    const addEntry = (customerId, assetInfo = "") => {
        if (!customerId) {
            message.warning("Vui lòng chọn khách hàng");
            return;
        }
        
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        const customer = availableCustomers.find(c => c.id === customerId);
        if (!customer) {
            message.error("Không tìm thấy khách hàng");
            return;
        }
        // ...
    };
    ============================================================================= */

    // ✅ THAY ĐỔI 6: addEntry thay vì addCustomer (NEW)
    const addEntry = (customerId, assetInfo = "", fallbackCustomer = null) => {
        if (!customerId) {
            message.warning("Vui lòng chọn khách hàng");
            return;
        }
        
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        let customer = availableCustomers.find(c => c.id === customerId);
        if (!customer) {
            if (fallbackCustomer) {
                customer = fallbackCustomer;
            } else {
                message.error("Không tìm thấy khách hàng");
                return;
            }
        }

        // ✅ Cho phép cùng customer_id, miễn là khác asset_info
        // Dòng đã hủy không tính: hủy rồi thêm lại đúng khách + tài sản là hợp lệ
        const isDuplicate = entries.some(e =>
            e.status !== "CANCELLED" &&
            Number(e.customer_id) === Number(customerId) &&
            (String(e.asset_info) === String(assetInfo) || String(e.charge_target_number) === String(assetInfo) || String(e.asset_id) === String(assetInfo)) &&
            Number(e.organization_id) === Number(valueOrg)
        );

        if (isDuplicate) {
            message.warning("Dòng thu này đã tồn tại (cùng khách hàng và tài sản)");
            return;
        }

        const entryId = generateEntryId();

        const newEntry = {
            id: entryId,
            customer_id: customerId,
            customer_name: customer.name,
            customer_phone: customer.phone,
            customer_discount_type: customer.discount_type ?? null,
            customer_discount_value: customer.discount_value ?? 0,
            organization_id: valueOrg ?? null,
            charge_target_number: assetInfo,
            asset_info: assetInfo,
            assets: customer.assets || []
        };

        setEntries([...entries, newEntry]);

        // Dựng giá trị cho dòng mới. Chỉ cần dòng này, không phải cả khối — setEntryValues
        // bên dưới đã gộp vào state hiện hành bằng prev.
        const newData = {};

        newData[entryId] = {
            discount_override: 0,
            charge_target_number: assetInfo,
            acc_payment_id: selectedAccount.id // THÊM DÒNG NÀY
        };

        fees.forEach(fee => {
            newData[entryId][fee.id] = fee.price || 0;
        });

        // Initialize discount mode
        setDiscountModes(prev => ({
            ...prev,
            [entryId]: 'auto'
        }));

        if (customer.discount_type === 'FIXED') {
            newData[entryId].discount_override = Number(customer.discount_value) || 0;
        } else {
            newData[entryId].discount_override = 0;
        }

        setEntryValues(prev => ({
            ...prev,
            [entryId]: newData[entryId]
        }));

        message.info(`Đã thêm dòng thu cho: ${customer.name}${assetInfo ? ` - ${assetInfo}` : ''}`);
    };

    /* ================== ĐOẠN CODE CŨ (ĐÃ YÊU CẦU COMMENT LẠI) ==================
    // ✅ THAY ĐỔI 7: addAllEntries - Thêm 1 dòng cho mỗi khách hàng (OLD)
    const addAllEntries = () => {
        if (availableCustomers.length === 0) {
          message.warning("Không có khách hàng nào để thêm");
          return;
        }
        
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        const notAddedCustomers = availableCustomers.filter(customer => 
          !entries.some(e => e.customer_id === customer.id)
        );
    
        if (notAddedCustomers.length === 0) {
          message.warning("Tất cả khách hàng đã được thêm rồi");
          return;
        }
    
        const newEntries = [];
        const newEntryValuesMap = {};

        notAddedCustomers.forEach(customer => {
            const entryId = generateEntryId();
            newEntries.push({
                id: entryId,
                customer_id: customer.id,
                customer_name: customer.name,
                customer_phone: customer.phone,
                customer_discount_type: customer.discount_type ?? null,
                customer_discount_value: customer.discount_value ?? 0,
                organization_id: valueOrg ?? null,
                charge_target_number: "",
                asset_info: "",
                assets: customer.assets || []
            });
    
            const entryData = {
                discount_override: 0,
                charge_target_number: "",
                acc_payment_id: selectedAccount.id
            };
            
            fees.forEach(fee => {
                entryData[fee.id] = fee.price || 0;
            });
            
            if (customer.discount_type === 'FIXED') {
                entryData.discount_override = Number(customer.discount_value) || 0;
            }
    
            setDiscountModes(prev => ({ ...prev, [entryId]: 'auto' }));
            newEntryValuesMap[entryId] = entryData;
        });

        setEntries(prev => [...prev, ...newEntries]);
        setEntryValues(prev => ({ ...prev, ...newEntryValuesMap }));
        message.info(`Đã thêm ${notAddedCustomers.length} dòng thu`);
    };
    ============================================================================= */

    // ✅ THAY ĐỔI 7: addAllEntries - Thêm 1 dòng cho mỗi khách hàng (NEW)
    const addAllEntries = () => {
        if (selectionMode === "CUSTOMER" && availableCustomers.length === 0) {
          message.warning("Không có khách hàng nào để thêm");
          return;
        }
        if (selectionMode === "ASSET" && availableAssets.length === 0) {
          message.warning("Không có tài sản nào để thêm");
          return;
        }
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        const newEntries = [];
        const newEntryValuesMap = {};
        // Chỉ chứa các dòng vừa thêm; state cũ được giữ qua prev ở setEntryValues bên dưới.
        const newData = {};

        if (selectionMode === "CUSTOMER") {
            const notAddedCustomers = availableCustomers.filter(
                (customer) => !entries.some((e) => Number(e.customer_id) === Number(customer.id))
            );
            if (notAddedCustomers.length === 0) {
                message.warning("Tất cả khách hàng đã được thêm rồi");
                return;
            }

            notAddedCustomers.forEach((customer) => {
                const entryId = generateEntryId();
                newEntries.push({
                    id: entryId,
                    customer_id: customer.id,
                    customer_name: customer.name,
                    customer_phone: customer.phone,
                    customer_discount_type: customer.discount_type ?? null,
                    customer_discount_value: customer.discount_value ?? 0,
                    organization_id: valueOrg ?? null,
                    charge_target_number: "",
                    asset_info: "",
                    assets: customer.assets || []
                });

                const entryData = {
                    discount_override: 0,
                    charge_target_number: "",
                    acc_payment_id: selectedAccount.id
                };

                fees.forEach((fee) => {
                    entryData[fee.id] = fee.price || 0;
                });

                if (customer.discount_type === "FIXED") {
                    entryData.discount_override = Number(customer.discount_value) || 0;
                }

                setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
                newEntryValuesMap[entryId] = entryData;
                newData[entryId] = entryData;
            });
            message.info(`Đã thêm ${notAddedCustomers.length} dòng thu Khách hàng`);

        } else {
            // Chế độ Tài sản
            const notAddedAssets = availableAssets.filter(
                (asset) => !entries.some((e) => Number(e.customer_id) === Number(asset.customer?.id) && 
                (String(e.asset_info) === String(asset.id) || String(e.charge_target_number) === String(asset.id) || String(e.asset_id) === String(asset.id)))
            );
            if (notAddedAssets.length === 0) {
                message.warning("Tất cả tài sản đã được thêm rồi");
                return;
            }

            notAddedAssets.forEach((asset) => {
                const customer = asset.customer;
                if (!customer) return;

                const entryId = generateEntryId();
                newEntries.push({
                    id: entryId,
                    customer_id: customer.id,
                    customer_name: customer.name,
                    customer_phone: customer.phone,
                    customer_discount_type: customer.discount_type ?? null,
                    customer_discount_value: customer.discount_value ?? 0,
                    organization_id: valueOrg ?? null,
                    charge_target_number: asset.id,
                    asset_info: asset.id,
                    assets: customer.assets || [asset]
                });

                const entryData = {
                    discount_override: 0,
                    charge_target_number: asset.id,
                    acc_payment_id: selectedAccount.id
                };

                fees.forEach((fee) => {
                    entryData[fee.id] = fee.price || 0;
                });

                if (customer.discount_type === "FIXED") {
                    entryData.discount_override = Number(customer.discount_value) || 0;
                }

                setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
                newEntryValuesMap[entryId] = entryData;
                newData[entryId] = entryData;
            });
            message.info(`Đã thêm ${notAddedAssets.length} dòng thu Tài sản`);
        }

        setEntries((prev) => [...prev, ...newEntries]);
        setEntryValues((prev) => ({ ...prev, ...newData, ...newEntryValuesMap }));
    };

    // ✅ THAY ĐỔI 8: removeEntry
    const removeEntry = (entryId) => {
        // OLD: remove khỏi UI (dẫn tới backend delete fee nếu không gửi id)
        // setEntries((prev) => prev.filter((e) => e.id !== entryId));
        // form.setFieldValue(["entryData", entryId], undefined);
        // setEntryValues(prev => {
        //     const next = { ...prev };
        //     delete next[entryId];
        //     return next;
        // });
        // setDiscountModes((prev) => {
        //   const newModes = { ...prev };
        //   delete newModes[entryId];
        //   return newModes;
        // });
        // message.info("Đã xóa dòng thu");

        // NEW:
        // - Nếu entry đã có trong DB (db_id) => hủy khoản thu (CANCELLED) để giữ lịch sử + auto-cancel giao dịch treo
        // - Nếu entry mới (db_id null) => vẫn xóa khỏi UI như cũ
        const entry = entries.find((e) => e.id === entryId);
        if (!entry) return;

        if (!entry.db_id) {
            setEntries((prev) => prev.filter((e) => e.id !== entryId));

            setEntryValues(prev => {
                const next = { ...prev };
                delete next[entryId];
                return next;
            });
            
            setDiscountModes((prev) => {
              const newModes = { ...prev };
              delete newModes[entryId];
              return newModes;
            });

            message.info("Đã xóa dòng thu (dòng mới)");
            return;
        }

        let reason = "";
        // Modal.confirm không có cách nào để onOk chặn việc đóng modal mà không reject, và
        // antd thì re-throw rejection đó (ant-design#6183) nên dev overlay đổ màn đỏ
        // "cancel_reason_required" mỗi lần bấm OK với ô lý do trống. Khoá luôn nút OK tới khi
        // có lý do: vừa bỏ được throw, vừa rõ ràng hơn cho người dùng.
        const modal = Modal.confirm({
            title: "Xác nhận hủy dòng thu",
            content: (
                <div>
                    <p>
                        Dòng thu của khách hàng <b>{entry.customer_name}</b> sẽ chuyển sang trạng thái <b>CANCELLED</b>.
                    </p>
                    <div style={{ marginTop: 8 }}>
                        <Text strong>Lý do hủy</Text>
                        <TextArea
                            rows={3}
                            placeholder="Nhập lý do hủy..."
                            onChange={(e) => {
                                reason = e.target.value;
                                modal.update({ okButtonProps: { disabled: !reason.trim() } });
                            }}
                        />
                    </div>
                </div>
            ),
            okText: "Hủy dòng thu",
            okType: "danger",
            okButtonProps: { disabled: true },
            cancelText: "Đóng",
            onOk: async () => {
                const r = reason.trim();

                // Không cho hủy nếu đã thanh toán/đã phát sinh thanh toán
                if (entry.status === "PAID" || Number(entry.amount_paid || 0) > 0) {
                    message.warning("Khoản thu đã thanh toán, không thể hủy!");
                    return;
                }

                try {
                    message.loading({ content: "Đang hủy khoản thu...", key: "cancelFeeRow" });
                    const response = await collectionAPIWithErrorHandling.cancelCustomerFee(entry.db_id, { cancel_reason: r });
                    message.success({ content: response?.data?.message || "Hủy khoản thu thành công!", key: "cancelFeeRow" });

                    // Update UI status => vẫn giữ dòng trong danh sách để audit
                    setEntries((prev) =>
                        prev.map((e) => (e.id === entryId ? { ...e, status: "CANCELLED" } : e))
                    );
                } catch (error) {
                    // Để rejection thoát khỏi onOk thì antd chỉ log ra console rồi giữ nguyên modal
                    // (ant-design#6183): người vận hành không thấy lý do thất bại, chỉ thấy thanh
                    // "Đang hủy khoản thu..." quay rồi tắt lặng lẽ. Phải tự báo lỗi ở đây.
                    message.error({ content: error.message || "Hủy khoản thu thất bại", key: "cancelFeeRow" });
                }
            },
        });
    };

    // ✅ THÊM: Copy entry (Duplicate)
    // const copyEntry = (sourceEntry) => {
    //     const newEntryId = generateEntryId();
        
    //     const newEntry = {
    //         ...sourceEntry,
    //         id: newEntryId,
    //         db_id: null, // Đây là entry mới, chưa có trong DB
    //         asset_info: "", // Clear asset info để user nhập mới
    //         charge_target_number: "",
    //         assets: sourceEntry.assets || []
    //     };

    //     setEntries([...entries, newEntry]);

    //     // Copy form data
    //     const currentData = form.getFieldValue("entryData") || {};
    //     const sourceData = currentData[sourceEntry.id] || {};
        
    //     const newData = { ...currentData };
    //     newData[newEntryId] = {
    //         ...sourceData,
    //         charge_target_number: "",
    //         acc_payment_id: sourceData.acc_payment_id // ĐẢM BẢO COPY ACC_PAYMENT_ID
    //     };

    //     form.setFieldValue("entryData", newData);

    //     // Copy discount mode
    //     setDiscountModes(prev => ({
    //         ...prev,
    //         [newEntryId]: prev[sourceEntry.id] || 'auto'
    //     }));

    //     message.info(`Đã sao chép dòng thu cho ${sourceEntry.customer_name}`);
    // };

    const copyEntry = (sourceEntry) => {
        const newEntryId = generateEntryId();
        
        const newEntry = {
            ...sourceEntry,
            id: newEntryId,
            db_id: null,
            asset_info: "",
            charge_target_number: "",
            // Dòng mới chưa gắn tài sản nào. Bỏ cả tài sản giữ lại của dòng nguồn (đã
            // chuyển chủ, xem giuTaiSanCuaKhoanThu) — nó chỉ để hiển thị khoản cũ, không
            // phải lựa chọn cho khoản mới của chủ cũ.
            asset_id: null,
            assets: (sourceEntry.assets || []).filter((a) => !a.giuLai),
            // Dòng sao chép là dòng MỚI, chưa có gì trong CSDL: phải trả trạng thái về
            // chưa thu. Bê nguyên status/amount_paid của dòng nguồn thì sao chép một dòng
            // đã thanh toán sẽ đẻ ra dòng mới bị khóa cứng, không nhập nổi ô nào.
            status: "UNPAID",
            amount_paid: 0,
        };

        setEntries(prev => [...prev, newEntry]);

        // // ✅ Lấy đúng form values tại thời điểm click
        // const currentData = form.getFieldsValue(true);
        // const entryData = currentData.entryData || {};
        // const sourceData = entryData[sourceEntry.id] || {};

        // // ✅ Copy toàn bộ fees từ source
        // const copiedData = { ...sourceData };
        // fees.forEach(fee => {
        //     copiedData[fee.id] = Number(sourceData[fee.id]) || 0;
        // });

        const sourceData = entryValues[sourceEntry.id] || {};

        const copiedData = { ...sourceData };
        fees.forEach(fee => {
            copiedData[fee.id] = Number(sourceData[fee.id]) || 0;
        });

        copiedData.charge_target_number = "";
        copiedData.acc_payment_id = sourceData.acc_payment_id || selectedAccount?.id || "";
        copiedData.discount_override = Number(sourceData.discount_override) || 0;

        setEntryValues(prev => ({
            ...prev,
            [newEntryId]: copiedData
        }));

        // Copy discount mode
        setDiscountModes(prev => ({
            ...prev,
            [newEntryId]: prev[sourceEntry.id] || 'auto'
        }));

        message.info(`Đã sao chép dòng thu cho ${sourceEntry.customer_name}`);
    };

    /**
     * Sinh mã khoản thu mới không đụng mã đang có.
     *
     * Bản cũ lấy `K${fees.length + 1}`: xoá một khoản ở giữa là độ dài mảng tụt xuống và
     * mã sinh ra trùng mã cũ (K1,K2,K3 → xoá K2 → thêm mới ra K3). Hai khoản cùng mã thì
     * dùng chung một ô giá trị, và khi lưu `fees` là object nên một khoản biến mất —
     * tiền gửi lên hụt đúng số tiền của khoản đó mà không có dấu hiệu gì.
     *
     * Tránh cả trùng TÊN vì file Excel lấy tên làm tiêu đề cột.
     */
    const nextFeeId = (currentFees) => {
        const usedIds = new Set(currentFees.map((f) => String(f.id)));
        const usedNames = new Set(
            currentFees.map((f) => String(f.name ?? "").trim().toLowerCase())
        );

        const maxIndex = currentFees.reduce((max, f) => {
            const matched = /^K(\d+)$/.exec(String(f.id));
            return matched ? Math.max(max, Number(matched[1])) : max;
        }, 0);

        let index = maxIndex + 1;
        while (usedIds.has(`K${index}`) || usedNames.has(`k${index}`)) index++;

        return `K${index}`;
    };

    const addFeeType = () => {
        const newFeeId = nextFeeId(fees);
        const newFee = { id: newFeeId, name: newFeeId, price: 0, tax: 0, unit: '' };
        setFees([...fees, newFee]);

        // const currentData = form.getFieldValue("entryData") || {};
        // Object.keys(currentData).forEach(entryId => {
        //     currentData[entryId][newFeeId] = 0;
        // });
        // form.setFieldValue("entryData", currentData);

        // ✅ Dùng entryValues làm source of truth
        setEntryValues(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(entryId => {
                updated[entryId] = { ...updated[entryId], [newFeeId]: 0 };
            });
            return updated;
        });

        message.info(`Đã thêm khoản thu: ${newFeeId}`);
    };

    const removeFeeType = (feeId) => {
        if (fees.length <= 1) {
            message.warning("Phải có ít nhất một khoản thu");
            return;
        }

        const feeToRemove = fees.find(f => f.id === feeId);
        setFees(fees.filter(f => f.id !== feeId));

        // const currentData = form.getFieldValue("entryData") || {};
        // Object.keys(currentData).forEach(entryId => {
        //     delete currentData[entryId][feeId];
        // });
        // form.setFieldValue("entryData", currentData);

        // ✅ Sync vào entryValues
        setEntryValues(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(entryId => {
                const copy = { ...updated[entryId] };
                delete copy[feeId];
                updated[entryId] = copy;
            });
            return updated;
        });
        message.info(`Đã xóa khoản thu: ${feeToRemove?.name}`);
    };

    const openEditFeeModal = (fee) => {
        setEditingFee(fee);
        setTempFeeName(fee.name);
        setTempFeePrice(fee.price || 0);
        setTempFeeTax(fee.tax || 0);
        setTempUnit(fee?.unit || '');
        setApplyPriceToAll(false);
        setFeeNameModalVisible(true);
    };

    const saveFeeNameChange = () => {
        if (!tempFeeName.trim()) {
            message.error("Tên khoản thu không được để trống");
            return;
        }

        // Tên khoản thu chính là tiêu đề cột trong file Excel. Hai khoản trùng tên thì
        // lúc nhập lại chỉ đọc được một cột, khoản còn lại mất số tiền vừa nhập.
        const nameKey = tempFeeName.trim().toLowerCase();
        const trungTen = fees.some(
            (fee) => fee.id !== editingFee.id && String(fee.name ?? "").trim().toLowerCase() === nameKey
        );
        if (trungTen) {
            message.error(`Đã có khoản thu tên "${tempFeeName.trim()}". Vui lòng đặt tên khác.`);
            return;
        }

        setFees(fees.map(fee =>
            fee.id === editingFee.id
                ? { ...fee, name: tempFeeName.trim(), price: tempFeePrice, tax: tempFeeTax, unit: tempUnit }
                : fee
        ));
        
        // if (applyPriceToAll) {
        //     const currentData = form.getFieldValue("entryData") || {};
        //     Object.keys(currentData).forEach(entryId => {
        //         currentData[entryId][editingFee.id] = tempFeePrice;
        //     });
        //     form.setFieldValue("entryData", currentData);
        //     message.info("Đã cập nhật tên và giá khoản thu cho tất cả dòng");
        // } else {
        //     message.info("Đã cập nhật tên khoản thu");
        // }

        if (applyPriceToAll) {
            // const currentData = form.getFieldValue("entryData") || {};
            // Object.keys(currentData).forEach(entryId => {
            //     currentData[entryId][editingFee.id] = tempFeePrice;
            // });
            // form.setFieldValue("entryData", currentData);

            // ✅ Sync vào entryValues để FeeInputCell hiển thị đúng
            setEntryValues(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(entryId => {
                    updated[entryId] = {
                        ...updated[entryId],
                        [editingFee.id]: tempFeePrice
                    };
                });
                return updated;
            });

            message.info("Đã cập nhật tên và giá khoản thu cho tất cả dòng");
        } else {
            message.info("Đã cập nhật tên khoản thu");
        }

        setFeeNameModalVisible(false);
        setEditingFee(null);
        setTempFeeName("");
        setTempFeeTax(0);
        setTempUnit("");
        setTempFeePrice(0);
        setApplyPriceToAll(false);
    };

    // const isCollectionEditable = () => {
    //     if (!originalData) return true;
    //     // OLD: chỉ cho sửa khi PLANNED
    //     // return originalData.status === 'PLANNED';

    //     // NEW: cho phép sửa khi đang diễn ra (ONGOING), chặn khi đã kết thúc (CLOSED)
    //     return originalData.status !== 'CLOSED';
    // };

    const canEditMainInfo = () => {
        if (!originalData) return true;
        
        // 1. Nếu đã kết thúc thì không được sửa bất cứ gì
        if (originalData.status === 'CLOSED') return false;

        // 2. Nếu là tài khoản SHOP, luôn có quyền sửa info
        if (userType === 'SHOP' || userType === 'WARD_LEADER') return true;

        // 3. Nếu là BM/STAFF: Chỉ sửa được info nếu CHÍNH HỌ là người tạo đợt thu này
        // if (isStaffScopedUser) {
        //     return Number(originalData.created_by) === Number(user?.data?.id);
        // }
        if (isStaffScopedUser && originalData.type_child === 'CHILD') {
            return false;
        }

        return true;
    };

    const canEditRows = () => {
        if (!originalData) return true;
        
        // 1. Nếu đã kết thúc thì không được sửa bất cứ gì
        if (originalData.status === 'CLOSED') return false;

        // 2. Nếu có quyền sửa info (Owner/Creator) thì đương nhiên sửa được rows
        if (canEditMainInfo()) return true;

        // 3. Nếu là BM/STAFF và đợt thu con này ĐANG MỞ KHÓA (is_locked=false) từ SHOP
        if (isStaffScopedUser && originalData.type_child === 'CHILD' && !isChildEditLocked) {
            return true;
        }

        return false;
    };

    const isCollectionEditable = canEditRows; // Fallback for general checks

    /**
     * Một DÒNG khoản thu có được sửa không.
     *
     * Khác với canEditRows() vốn chỉ xét cả đợt thu và quyền người dùng: dòng nào đã
     * phát sinh tiền (PAID/PARTIAL) hoặc đã hủy thì backend bỏ qua hoàn toàn khi lưu
     * (CollectionService::updateCustomerFees dừng ở `continue` với mọi status khác
     * UNPAID). Trước đây ô nhập vẫn mở nên người dùng sửa xong thấy báo "thành công"
     * mà dữ liệu không đổi — khóa ở đây để không còn hiểu nhầm đó.
     *
     * Khóa cả số nhà, tài sản và tài khoản nhận tiền chứ không riêng số tiền: chúng là
     * thông tin của giao dịch đã phát sinh, sửa sẽ làm lệch đối soát.
     */
    const isRowEditable = (record) =>
        isCollectionEditable() && (record?.status ?? "UNPAID") === "UNPAID";

    /** Lý do dòng bị khóa, để hiện tooltip thay vì khóa im lặng. */
    const rowLockReason = (record) => {
        if (!isCollectionEditable()) return "Đợt thu này không còn chỉnh sửa được";

        const status = record?.status ?? "UNPAID";
        if (status === "CANCELLED") return "Dòng thu đã hủy, không thể chỉnh sửa";
        if (status !== "UNPAID") return "Khoản thu đã phát sinh thanh toán, không thể chỉnh sửa";

        return "";
    };

    const feeDefinitionsForColumns = fees;

    // ✅ THAY ĐỔI 9: Columns dùng entry thay vì customer
    const columns = React.useMemo(() => [
    // const columns = [
        {
            title: "Khách hàng",
            dataIndex: "customer_name",
            key: "customer_name",
            width: 180,
            // Ghim lại khi cuộn ngang: bảng rộng hơn màn hình nhiều, không ghim thì nhập
            // tới cột phí thứ ba là không còn biết đang nhập cho ai.
            fixed: "left",
            render: (text, record) => {
                const customerInfoContent = (
                    <div style={{ maxWidth: 320 }}>
                        <div style={{ marginBottom: 5 }}>
                            <Text strong style={{ fontSize: 16 }}>{record.customer_name}</Text>
                        </div>
            
                        <Divider style={{ margin: '8px 0' }} />
            
                        <div style={{ marginBottom: 5 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Số điện thoại:</Text>
                            <div><Text strong>{record.customer_phone}</Text></div>
                        </div>
            
                        {record.customer_discount_type && (
                            <>
                                <div style={{ marginBottom: 5 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Loại giảm trừ:</Text>
                                    <div>
                                    <Text strong>
                                        {record.customer_discount_type === 'PERCENTAGE' ? 'Phần trăm (%)' : 'Cố định (đ)'}
                                    </Text>
                                    </div>
                                </div>
                
                                <div style={{ marginBottom: 5 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Giá trị giảm trừ:</Text>
                                    <div>
                                    <Text strong style={{ color: '#A8681E' }}>
                                        {record.customer_discount_type === 'PERCENTAGE'
                                        ? `${record.customer_discount_value}%`
                                        : `${Number(record.customer_discount_value || 0).toLocaleString('vi-VN')} đ`
                                        }
                                    </Text>
                                    </div>
                                </div>
                            </>
                        )}
            
                        {!record.customer_discount_type && (
                            <Alert
                            message="Không có giảm trừ"
                            type="info"
                            showIcon
                            style={{ fontSize: 12, padding: '4px 8px' }}
                            />
                        )}
            
                        <Divider style={{ margin: '8px 0' }} />
            
                        <Button
                            type="link"
                            size="small"
                            icon={<UserOutlined />}
                            style={{ padding: 0 }}
                            onClick={(e) => {
                            e.stopPropagation(); // Ngăn đóng popover
                            Modal.confirm({
                                title: "Xem chi tiết khách hàng",
                                icon: <ExclamationCircleOutlined />,
                                content: `Bạn có muốn chuyển đến trang chi tiết của khách hàng "${record.customer_name}" không?`,
                                okText: "Xem chi tiết",
                                cancelText: "Hủy",
                                onOk: () => {
                                navigate(`/customers/information/${record.customer_id}`);
                                },
                            });
                            }}
                        >
                            Xem chi tiết khách hàng
                        </Button>
                    </div>
                );
        
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <div style={{ flex: 1 }}>
                        <div className="font-medium">{text}</div>
                        <Text type="secondary" className="text-xs">
                        {record.customer_phone}
                        </Text>
                    </div>
        
                    <Popover
                        content={customerInfoContent}
                        title={null}
                        trigger="hover"
                        placement="rightTop"
                        overlayStyle={{ maxWidth: 350 }}
                    >
                        <Button
                        type="text"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        style={{
                            color: '#0070D7',
                            padding: 4,
                            height: 'auto'
                        }}
                        />
                    </Popover>
                    </div>
                );
            },
        },
        ...(!sortByOrg ?
            [
                {
                    title: "Đơn vị",
                    dataIndex: "org_id",
                    key: "org_id",
                    width: 180,
                    render: (text, record) => {
                        const orgItem = treeData.find(c => Number(c.id) === Number(record.organization_id));
                        
                        if (!orgItem) {
                            return <Text type="secondary">Không xác định</Text>;
                        }
                        
                        return (
                            <Popover
                                content={
                                    <div>
                                    <div><Text strong>ID:</Text> {orgItem.id}</div>
                                    <div><Text strong>Tên:</Text> {orgItem.title}</div>
                                    </div>
                                }
                                title="Thông tin đơn vị"
                                trigger="hover"
                            >
                                <Text style={{ cursor: 'pointer' }}>
                                    {orgItem.title}
                                </Text>
                            </Popover>
                        );
                    }
                },
            ] : []
        ),
        // ...(collectionType !== 'CUSTOMER' ?
        // [
        // {
        //     title: "Tài sản",
        //     key: "charge_target_number",
        //     width: 280,
        //     align: "center",
        //     render: (_, record) => (
        //         <Form.Item
        //             name={["entryData", record.id, "charge_target_number"]}
        //             style={{ margin: 0 }}
        //         >
        //             <Input
        //                 placeholder="Tài sản"
        //                 className="w-full"
        //                 disabled={!isCollectionEditable() || collectionType === 'CUSTOMER'}
        //             />
        //         </Form.Item>
        //     ),
        // },
        // ] : []
        // ),
        ...(collectionType !== 'CUSTOMER' ?
        [
        {
            title: "Tài sản",
            key: "charge_target_number",
            width: 210,
            align: "center",
            // render: (_, record) => {
            //     const customerInAvailable = availableCustomers.find(c => c.id === record.customer_id);
            //     const customerAssets = customerInAvailable?.assets || record.assets || [];
                
            //     return (
            //         <Form.Item
            //             name={["entryData", record.id, "charge_target_number"]}
            //             style={{ margin: 0, width: '100%' }}
            //         >
            //             {customerAssets.length > 0 ? (
            //                 <Select
            //                     placeholder="Chọn tài sản"
            //                     className="w-full"
            //                     allowClear
            //                     showSearch
            //                     disabled={!isCollectionEditable()}
            //                     filterOption={(input, option) =>
            //                         (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            //                     }
            //                     options={customerAssets.map(asset => ({
            //                         value: asset.id,
            //                         label: `${asset.name} ${asset.street}`
            //                     }))}
            //                 />
            //             ) : (
            //                 <Input
            //                     placeholder="Tài sản"
            //                     className="w-full"
            //                     disabled={!isCollectionEditable() || collectionType === 'CUSTOMER'}
            //                 />
            //             )}
            //         </Form.Item>
            //     );
            // },
            render: (_, record) => {
                // const customerInAvailable = availableCustomers.find(c => c.id === record.customer_id);
                const customerInAvailable = availableCustomers.find(
                    c => Number(c.id) === Number(record.customer_id)
                );
                
                // ✅ Ưu tiên availableCustomers, fallback về record.assets
                const customerAssets = giuTaiSanCuaKhoanThu(
                    (customerInAvailable?.assets?.length > 0)
                        ? customerInAvailable.assets
                        : (record.assets || []),
                    record
                );

                // Giá trị hiện tại lấy từ entryValues — nguồn duy nhất kể từ khi bỏ
                // entryData khỏi store của antd Form (xem ghi chú ở fetchCollectionData).
                const currentValue = entryValues[record.id]?.charge_target_number;

                // ✅ Nếu có assets -> Select, không có -> Input
                // ✅ Nhưng nếu đang có giá trị text (số căn hộ cũ) mà không map được asset -> vẫn cho nhập text
                const hasAssets = customerAssets.length > 0;
                const currentIsAssetId = hasAssets && customerAssets.some(a => a.id === currentValue);
                

                return (
                    <div style={{ margin: 0, width: '100%' }}>
                        {/* {hasAssets ? (
                            <Select
                                placeholder="Chọn tài sản"
                                className="w-full"
                                allowClear
                                showSearch
                                disabled={!isCollectionEditable()}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={customerAssets.map(asset => ({
                                    value: asset.id,
                                    label: `${asset.name || ''} ${asset.street || ''}`.trim()
                                }))}
                            />
                        ) : (
                            <Input
                                placeholder="Nhập tài sản"
                                className="w-full"
                                disabled={!isCollectionEditable()}
                            />
                        )} */}
                        {hasAssets ? (
                            <Select
                                placeholder="Chọn tài sản"
                                className="w-full"
                                allowClear
                                showSearch
                                disabled={!isRowEditable(record)}
                                title={rowLockReason(record)}
                                value={currentValue || undefined}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={customerAssets.map(asset => ({
                                    value: asset.id,
                                    // label: `${asset.name || ''} ${asset.street || ''}`.trim()
                                    label: `${asset.name || ''}`.trim()
                                }))}
                                onChange={(val) => {
                                    // ✅ Sync vào entryValues khi chọn tài sản
                                    updateEntryValue(record.id, 'charge_target_number', val ?? '');
                                }}
                            />
                        ) : (
                            <TextCell
                                initialValue={currentValue ?? ''}
                                placeholder="Nhập tài sản"
                                disabled={!isRowEditable(record)}
                                onCommit={(val) => {
                                    // ✅ Sync vào entryValues khi nhập tay
                                    updateEntryValue(record.id, 'charge_target_number', val ?? '');
                                }}
                            />
                        )}
                    </div>
                );
            },
        },
        ] : []
        ),
        ...feeDefinitionsForColumns.map((feeDefinition) => ({
            title: (
                <Space direction="vertical" size={0}>
                <Space>
                    <span
                        className={isCollectionEditable() ? "cursor-pointer hover:text-blue-600 hover:underline" : ""}
                        onClick={() => isCollectionEditable() && openEditFeeModal(feeDefinition)}
                        title={isCollectionEditable() ? "Click để đổi tên" : feeDefinition.name}
                    >
                        {feeDefinition.name}
                    </span>
                    {isCollectionEditable() && (
                        <Popconfirm
                            title="Xóa khoản thu này?"
                            description="Dữ liệu của khoản thu sẽ bị xóa"
                            onConfirm={() => removeFeeType(feeDefinition.id)}
                            okText="Xóa"
                            cancelText="Hủy"
                            disabled={feeDefinition.length <= 1}
                        >
                            <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                disabled={feeDefinition.length <= 1}
                                className="opacity-60 hover:opacity-100"
                            />
                        </Popconfirm>
                    )}
                </Space>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Thuế {feeDefinition?.tax ?? 0}%{feeDefinition?.unit ? ` · ${feeDefinition.unit}` : ""}
                    </Text>
                </Space>
            ),
            dataIndex: feeDefinition.id,
            key: feeDefinition.id,
            width: 180,
            render: (_, record) => {
                const val = entryValues[record.id]?.[feeDefinition.id] ?? 0;
                return (
                    <FeeInputCell
                        initialValue={val}
                        disabled={!isRowEditable(record)}
                        onCommit={(v) => {
                            updateEntryValue(record.id, feeDefinition.id, v);
                        }}
                    />
                );
            },
        })),
        // {
        //     title: "Giảm trừ",
        //     key: "discount_override",
        //     width: 130,
        //     align: "right",
        //     render: (_, record) => {
        //         return (
        //             <Form.Item
        //                 shouldUpdate={(prev, curr) => {
        //                     const prevData = prev.entryData?.[record.id];
        //                     const currData = curr.entryData?.[record.id];
        //                     return JSON.stringify(prevData) !== JSON.stringify(currData);
        //                 }}
        //                 noStyle
        //                 preserve={true}  // ✅ Thêm dòng này
        //             >
        //                 {({ getFieldValue, setFieldValue }) => {
        //                     const entryData = getFieldValue(["entryData", record.id]) || {};
        //                     const currentMode = discountModes[record.id] || "auto";

        //                     const subtotal = fees.reduce(
        //                         (sum, fee) => sum + (Number(entryData[fee.id]) || 0),
        //                         0
        //                     );

        //                     let autoDiscount = 0;

        //                     if (record.customer_discount_type === "PERCENTAGE") {
        //                         autoDiscount = Math.round(
        //                             subtotal * (Number(record.customer_discount_value) || 0) / 100
        //                         );
        //                     } else if (record.customer_discount_type === "FIXED") {
        //                         autoDiscount = Number(record.customer_discount_value) || 0;
        //                     }

        //                     // if (currentMode === "auto" && entryData.discount_override !== autoDiscount) {
        //                     //     setFieldValue(
        //                     //         ["entryData", record.id, "discount_override"],
        //                     //         autoDiscount
        //                     //     );
        //                     // }
        //                     if (currentMode === "auto" && entryData.discount_override !== autoDiscount) {
        //                         setFieldValue(
        //                             ["entryData", record.id, "discount_override"],
        //                             autoDiscount
        //                         );
        //                         // ✅ Sync vào entryValues khi auto tính
        //                         updateEntryValue(record.id, 'discount_override', autoDiscount);
        //                     }

        //                     return (
        //                         <div>
        //                             <Form.Item
        //                                 name={["entryData", record.id, "discount_override"]}
        //                                 style={{ margin: 0 }}
        //                                 preserve={true}  // ✅ Thêm dòng này
        //                             >
        //                                 <InputNumber
        //                                     min={0}
        //                                     max={subtotal}
        //                                     disabled={currentMode === "auto" || !isCollectionEditable()} // THÊM điều kiện editable
        //                                     formatter={(value) =>
        //                                         `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        //                                     }
        //                                     parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
        //                                     placeholder="0"
        //                                     className="w-full"
        //                                     style={{ width: "100%" }}
        //                                     onBlur={(e) => {
        //                                         // ✅ Sync vào entryValues khi nhập tay
        //                                         const raw = e.target.value.replace(/,/g, '');
        //                                         const v = Number(raw) || 0;
        //                                         updateEntryValue(record.id, 'discount_override', v);
        //                                     }}
        //                                     addonAfter={
        //                                         <div
        //                                             style={{
        //                                                 display: "flex",
        //                                                 alignItems: "center",
        //                                                 gap: 6,
        //                                                 minWidth: 60,
        //                                             }}
        //                                         >
        //                                             <Switch
        //                                                 checkedChildren="Tự động"
        //                                                 unCheckedChildren="Thủ công"
        //                                                 checked={currentMode === "auto"}
        //                                                 disabled={!isCollectionEditable()} // THÊM điều kiện này
        //                                                 // onChange={(checked) => {
        //                                                 //     const newMode = checked ? "auto" : "manual";
        //                                                 //     setDiscountModes((prev) => ({
        //                                                 //         ...prev,
        //                                                 //         [record.id]: newMode,
        //                                                 //     }));

        //                                                 //     if (checked) {
        //                                                 //         setFieldValue(
        //                                                 //             ["entryData", record.id, "discount_override"],
        //                                                 //             autoDiscount
        //                                                 //         );
        //                                                 //     }
        //                                                 // }}
        //                                                 onChange={(checked) => {
        //                                                     const newMode = checked ? "auto" : "manual";
        //                                                     setDiscountModes((prev) => ({
        //                                                         ...prev,
        //                                                         [record.id]: newMode,
        //                                                     }));

        //                                                     if (checked) {
        //                                                         setFieldValue(
        //                                                             ["entryData", record.id, "discount_override"],
        //                                                             autoDiscount
        //                                                         );
        //                                                         // ✅ Sync vào entryValues khi bật auto
        //                                                         updateEntryValue(record.id, 'discount_override', autoDiscount);
        //                                                     }
        //                                                 }}
        //                                             />
        //                                             <span style={{ fontSize: 12, color: "#999" }}>
        //                                                 {record.customer_discount_type === "PERCENTAGE" ? "%" : "đ"}
        //                                             </span>
        //                                         </div>
        //                                     }
        //                                 />
        //                             </Form.Item>
        //                         </div>
        //                     );
        //                 }}
        //             </Form.Item>
        //         );
        //     },
        // },

        {
            title: "Giảm trừ",
            key: "discount_override",
            width: 150,
            align: "right",
            render: (_, record) => (
                <DiscountCell
                    record={record}
                    fees={fees}
                    entryValues={entryValues}
                    discountMode={discountModes[record.id]}
                    isEditable={isRowEditable(record)}
                    // onCommit={(v) => {
                    //     updateEntryValue(record.id, 'discount_override', v);
                    //     form.setFieldValue(["entryData", record.id, "discount_override"], v);
                    // }}
                    // onModeChange={(newMode) => {
                    //     setDiscountModes(prev => ({
                    //         ...prev,
                    //         [record.id]: newMode
                    //     }));
                    // }}
                    onCommit={(v) => handleDiscountCommit(record.id, v)}  // ✅ handleDiscountCommit stable
                    onModeChange={(newMode) => handleModeChange(record.id, newMode)}  // ✅ stable
                
                />
            ),
        },
        {
            title: "Tổng cộng",
            key: "total",
            width: 190,
            align: "right",
            render: (_, record) => {
                // ✅ Đọc từ entryValues thay vì form
                const eData = entryValues[record.id] || {};
                const subtotal = fees.reduce(
                    (sum, fee) => sum + (Number(eData[fee.id]) || 0),
                    0
                );
                const discountValue = Number(eData.discount_override) || 0;
                const total = Math.max(0, subtotal - discountValue);

                return (
                    <Text strong className="text-blue-600">
                        {total.toLocaleString("vi-VN")} đ
                    </Text>
                );
            },
        },
        {
            title: "Tài khoản",
            dataIndex: "acc_payment_id",
            key: "acc_payment_id",
            width: 260,
            render: (_, record) => {
                const accValue = entryValues[record.id]?.acc_payment_id;

                return (
                    <div style={{ margin: 0, width: '100%' }}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Chọn tài khoản"
                            allowClear
                            showSearch
                            // Ô này trước đây không khóa theo điều kiện nào, kể cả khi đợt thu
                            // đã đóng. Tài khoản nhận tiền của khoản thu đã phát sinh thanh
                            // toán là dữ liệu đối soát, không được sửa.
                            disabled={!isRowEditable(record)}
                            title={rowLockReason(record)}
                            // Ô trống thì viền đỏ ngay, thay cho rule "required" của Form.Item
                            // trước đây. Rule đó chỉ chạm tới các dòng đang hiển thị trong
                            // danh sách ảo nên vốn không chặn được gì; chặn thật nằm ở
                            // handleSubmit, kiểm mọi dòng trước khi gửi.
                            // Vẫn báo đỏ khi trống kể cả lúc khóa: handleSubmit chặn mọi dòng
                            // thiếu tài khoản, nên phải nhìn ra được dòng nào đang thiếu.
                            status={accValue ? undefined : "error"}
                            value={accValue || undefined}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={accounts.map((item) => ({
                                value: item.id,
                                label: item.name,
                            }))}

                            onChange={(val) => {
                                // ✅ Sync vào entryValues khi chọn tài khoản
                                updateEntryValue(record.id, 'acc_payment_id', val ?? '');
                            }}
                        />
                    </div>
                );
            },
        },
        {
            title: "Thao tác",
            key: "action",
            width: 100,
            align: "center",
            render: (_, record) => (
                isCollectionEditable() && (
                    <Space size="small">
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={() => copyEntry(record)}
                            title="Sao chép dòng"
                            disabled={record.status === "CANCELLED"}
                        />
                        <Popconfirm
                            title={record.db_id ? "Hủy dòng thu?" : "Xóa dòng thu?"}
                            description={
                                record.db_id
                                    ? "Dòng thu này sẽ chuyển sang CANCELLED (không xóa) và tự hủy giao dịch treo nếu có."
                                    : "Xóa dòng thu mới này khỏi danh sách (chưa lưu DB)"
                            }
                            onConfirm={() => removeEntry(record.id)}
                            okText={record.db_id ? "Hủy" : "Xóa"}
                            cancelText="Hủy"
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                disabled={record.status === "PAID" || Number(record.amount_paid || 0) > 0}
                            />
                        </Popconfirm>
                    </Space>
                )
            ),
        },
    // ];
    ], [
        fees,
        entryValues,
        discountModes,
        accounts,
        treeData,
        sortByOrg,
        collectionType,
        availableCustomers,
        updateEntryValue,
        handleDiscountCommit,
        handleModeChange,
    ]);

    // ✅ THAY ĐỔI 10: tableDataSource dùng entries
    // const tableDataSource = entries.map(entry => ({
    //     key: entry.id,
    //     ...entry,
    // }));
    
    // Xử lý lọc và sắp xếp VỚI GROUPED
    const { zeroEntries, zeroFilterIds, showZero, clearZero, toggleZero } =
        useZeroFeeFilter(entries, entryValues, fees);

    const tableDataSource = React.useMemo(() => {
        let data = entries.map(entry => ({
            key: entry.id,
            ...entry,
        }));

        if (zeroFilterIds) {
            data = data.filter(item => zeroFilterIds.has(item.id));
        }

        // Lọc theo đơn vị
        if (filterOrgId) {
            data = data.filter(item => Number(item.organization_id) === Number(filterOrgId));
        }
    
        // Lọc theo tên hoặc số điện thoại
        if (filterText.trim()) {
            const searchText = filterText.toLowerCase().trim();

            // Phải ép String trước khi hạ chữ thường: `asset_info` giữ id tài sản, mà id
            // là SỐ với mọi dòng dựng trong phiên (nhập Excel, nút thêm tài sản) — chỉ
            // dòng nạp từ API mới là chuỗi. Optional chaining chỉ chặn null/undefined nên
            // `(3545)?.toLowerCase()` vẫn ném TypeError giữa lúc render, và app không có
            // ErrorBoundary nên cả cây bị gỡ -> trắng màn hình.
            const match = (value) => String(value ?? "").toLowerCase().includes(searchText);

            data = data.filter(item =>
                match(item.customer_name) ||
                match(item.customer_phone) ||
                match(item.asset_info)
            );
        }
    
        // ✅ NẾU BẬT SẮP XẾP -> NHÓM THEO ĐơN VỊ
        if (sortByOrg) {
            // Nhóm entries theo organization_id
            const grouped = data.reduce((acc, entry) => {
            const orgId = entry.organization_id || 'unknown';
            if (!acc[orgId]) {
                acc[orgId] = [];
            }
            acc[orgId].push(entry);
            return acc;
            }, {});
            
            // Sắp xếp các nhóm theo tên đơn vị
            const sortedGroups = Object.entries(grouped).sort(([orgIdA], [orgIdB]) => {
            const orgA = treeData.find(c => Number(c.id) === Number(orgIdA));
            const orgB = treeData.find(c => Number(c.id) === Number(orgIdB));
            
            const nameA = orgA?.title || 'Không xác định';
            const nameB = orgB?.title || 'Không xác định';
            
            // numeric: true để "Khu phố 8" đứng trước "Khu phố 16"
            return nameA.localeCompare(nameB, 'vi', { numeric: true });
            });
            
            // Flatten lại với header cho mỗi nhóm
            const result = [];
            sortedGroups.forEach(([orgId, items]) => {
            const orgItem = treeData.find(c => Number(c.id) === Number(orgId));
            
            // Thêm header row
            result.push({
                key: `header_${orgId}`,
                isHeader: true,
                orgName: orgItem?.title || 'Không xác định',
                itemCount: items.length
            });
            
            // Thêm các items
            result.push(...items);
            });
            
            return result;
        }
    
        // ✅ NẾU TẮT SẮP XẾP -> TRẢ VỀ DANH SÁCH PHẲNG
        return data;
    }, [entries, filterOrgId, filterText, sortByOrg, treeData, zeroFilterIds]);

    // Kéo hàng tiêu đề chạy ngang theo thân bảng. Gắn thẳng listener trên DOM thay vì
    // dùng onScroll của danh sách ảo: onScroll ở đó chỉ báo vị trí cuộn dọc.
    React.useEffect(() => {
        const body = tableBodyRef.current;
        const header = tableHeaderRef.current;
        if (!body || !header) return;

        const syncHeader = () => {
            header.scrollLeft = body.scrollLeft;
        };

        body.addEventListener("scroll", syncHeader, { passive: true });
        syncHeader();

        return () => body.removeEventListener("scroll", syncHeader);
    }, [tableDataSource.length, currentStep]);

    // Bảng cao theo cửa sổ. Chỉ đặt lại state khi con số thật sự đổi để kéo giãn cửa sổ
    // không bắt cả bảng vẽ lại liên tục.
    React.useEffect(() => {
        const onResize = () =>
            setTableMaxHeight((prev) => {
                const next = computeTableHeight();
                return next === prev ? prev : next;
            });

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    
    // Lấy danh sách đơn vị duy nhất từ entries
    const uniqueOrgs = React.useMemo(() => {
        const orgIds = [...new Set(entries.map(e => e.organization_id).filter(Boolean))];
        return orgIds.map(id => {
            const org = treeData.find(c => Number(c.id) === Number(id));
            return {
            id,
            name: org?.title || 'Không xác định'
            };
        }).sort((a, b) => a.name.localeCompare(b.name, 'vi', { numeric: true }));
    }, [entries, treeData]);

    const visibleEntryCount = tableDataSource.filter((item) => !item.isHeader).length;

    // Mô tả ngắn của từng bước, hiện dưới tiêu đề trang. Bản cũ còn title + focus cho
    // khối hero riêng — khối đó đã bỏ vì lặp lại đúng nội dung Steps và ô bắt buộc.
    const stepGuides = [
        { description: "Rà soát lại thông tin cơ bản trước khi cập nhật danh sách thu." },
        { description: "Điều chỉnh khách hàng hoặc tài sản và kiểm tra tài khoản thanh toán." },
        { description: "Đối chiếu số liệu cuối cùng trước khi lưu thay đổi." },
    ];
    const currentStepGuide = stepGuides[currentStep] || stepGuides[0];

    // Đợt thu đang sửa đã có nội dung chuyển khoản hoặc mô tả thì mở sẵn khối
    // "Thanh toán & Ghi chú", không để người dùng tưởng dữ liệu bị mất.
    const hasExtraInfo = Boolean(originalData?.transfer_content || originalData?.description);
    const getCollectionSummary = () =>
        entries.reduce(
            (totals, entry) => {
                const eData = entryValues[entry.id] || {};
                const subtotal = fees.reduce(
                    (sum, fee) => sum + (Number(eData[fee.id]) || 0),
                    0
                );
                const discount = Number(eData.discount_override) || 0;

                totals.grandSubtotal += subtotal;
                totals.grandDiscount += discount;
                totals.grandTotal += Math.max(0, subtotal - discount);

                return totals;
            },
            { grandSubtotal: 0, grandDiscount: 0, grandTotal: 0 }
        );

    // ✅ THAY ĐỔI 11: handleSubmit format data theo entry
    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);

            // Hỏi 0đ TRƯỚC cảnh báo đợt đang chạy: chọn "Xem các dòng này" thì khỏi phải qua thêm hộp thoại thứ hai
            const zeroList = findZeroEntries(entries, entryValues, fees);
            if (zeroList.length > 0 && !(await confirmZeroEntries(zeroList.length))) {
                // Về bước nhập liệu, bỏ các lọc khác để thấy đủ dòng 0đ
                setFilterText("");
                setFilterOrgId(null);
                showZero(zeroList);
                setCurrentStep(1);
                return;
            }

            // Cảnh báo nếu đợt thu đang chạy
            if (originalData?.status === 'ONGOING') {
                const isConfirmed = await new Promise((resolve) => {
                    Modal.confirm({
                        title: 'Xác nhận cập nhật đợt thu đang chạy',
                        icon: <ExclamationCircleOutlined style={{ color: '#A8681E' }} />,
                        width: 500,
                        content: (
                            <div>
                                <p>Đợt thu này <b>đang diễn ra</b>. Việc cập nhật có thể làm thay đổi số tiền hoặc thông tin mà khách hàng đã nhận được qua tin nhắn/phiếu thu.</p>
                                <p>Bạn có chắc chắn muốn áp dụng các thay đổi này không?</p>
                            </div>
                        ),
                        okText: 'Tôi xác nhận cập nhật',
                        cancelText: 'Hủy bỏ',
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });
                if (!isConfirmed) {
                    setSubmitting(false);
                    return;
                }
            }

            if (!values.name?.trim()) {
                message.error("Vui lòng nhập tên đợt thu");
                return;
            }

            if (!values.dateRange || values.dateRange.length !== 2) {
                message.error("Vui lòng chọn thời gian bắt đầu và kết thúc");
                return;
            }

            if (entries.length === 0) {
                message.error("Vui lòng thêm ít nhất một dòng thu");
                return;
            }

            // Lưới chống bỏ sót: dòng nào chưa có tài khoản thì lấy tài khoản đang chọn.
            // Thay cho cách cũ là rải sẵn xuống mọi dòng mỗi lần đổi lựa chọn — cách đó
            // đè cả những dòng đã gán đúng tài khoản của khối khác.
            const thieuTaiKhoan = entries.filter(
                (entry) => !(entryValues[entry.id]?.acc_payment_id) && selectedAccount?.id
            );
            if (thieuTaiKhoan.length > 0) {
                message.info(`Đã tự điền tài khoản "${selectedAccount.name}" cho ${thieuTaiKhoan.length} dòng chưa chọn`);
            }

            const customer_fees = entries.map(entry => {
            // const customer_fees = entries.map(entry => {
                // const eData = entryData[entry.id] || {};

                // // Safety fallback: Nếu thiếu acc_payment_id nhưng đã chọn tài khoản chung -> Dùng tài khoản chung
                // if (!eData.acc_payment_id && selectedAccount) {
                //     eData.acc_payment_id = selectedAccount.id;
                // }

                // if (!eData.acc_payment_id) {
                //     throw new Error(`Vui lòng chọn tài khoản thanh toán cho khách hàng: ${entry.customer_name}`);
                // }
                // const eData = { ...(entryData[entry.id] || {}) }; // clone tránh mutate trực tiếp
                const eData = { ...(entryValues[entry.id] || {}) };

                // Ưu tiên: acc trong form > selectedAccount chung
                const accPaymentId = eData.acc_payment_id || selectedAccount?.id || null;

                if (!accPaymentId) {
                    throw new Error(`Vui lòng chọn tài khoản thanh toán cho khách hàng: ${entry.customer_name}`);
                }

                const assetVal = eData.charge_target_number;
                const selectedAsset = entry.assets?.find(a => a.id === assetVal);

                return {
                    id: entry.db_id || null, // ID từ DB, null nếu là entry mới
                    customer_id: entry.customer_id,
                    asset_id: selectedAsset ? selectedAsset.id : null,
                    fees: fees.reduce((feeObj, fee) => {
                        feeObj[fee.id] = Number(eData[fee.id]) || 0;
                        return feeObj;
                    }, {}),
                    discount_override: Number(eData.discount_override) || 0,
                    organization_id: Number(entry.organization_id) || null,
                    charge_target_number: selectedAsset ? selectedAsset.name : assetVal,
                    // acc_payment_id: eData.acc_payment_id // THÊM DÒNG NÀY
                    acc_payment_id: accPaymentId
                };
            });

            const collectionData = collectionHelpers.formatCollectionData({
                name: values.name.trim(),
                description: values.description?.trim() || "",
                start_date: values.dateRange[0].format("YYYY-MM-DD"),
                end_date: values.dateRange[1].format("YYYY-MM-DD"),
                send_date: values.send_date ? values.send_date.format("YYYY-MM-DD") : null,
                status: values.status || "PLANNED",
                customer_fees,
                fee_definitions: fees.map(fee => ({
                    id: fee.id,
                    name: fee.name,
                    tax: fee.tax,
                    unit: fee.unit,
                })),
                is_send_zns: values.is_send_zns,
                is_locked: values.is_locked ?? true,
                send_schedule_at: valueSendScheduleAt ? dayjs(valueSendScheduleAt).format("HH:mm:ss") : null,
                before_date: values.before_date ?? null,
                after_date: values.after_date ?? null,
                charge_target_type: values.collection_type ?? originalData?.charge_target_type,
                // invoice_cnf_id: values.invoice_cnf_id,
                collection_category_id: values.collection_category_id ?? originalData?.collection_category_id,
                transfer_content: values.transfer_content?.trim() || null,
            });

            const validation = collectionHelpers.validateCollectionData(collectionData);
            if (!validation.isValid) {
                message.error("Dữ liệu không hợp lệ: " + Object.values(validation.errors)[0]);
                return;
            }


            const res = await collectionAPIWithErrorHandling.updateCollection(id, collectionData);

            // Đợt thu lớn: backend trả 202 và xử lý ở queue để không đụng trần 30s của
            // PHP-FPM. Giữ trạng thái "đang lưu" và theo dõi tiến độ tới khi xong.
            if (res?.data?.queued) {
                await waitForBackgroundUpdate(res.data.data?.total);
                return;
            }

            message.success("Cập nhật đợt thu thành công!");
            fetchCollectionData();

            //   navigate("/collections");

        } catch (error) {
            message.error("Lỗi khi cập nhật đợt thu: " + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Bắt lại tiến trình nền đang chạy khi mở/tải lại trang.
     *
     * Trạng thái sống trong cache 2 giờ ở backend, nhưng trước đây chỉ có đường bấm Lưu mới
     * theo dõi nó. Ai F5 giữa chừng sẽ thấy màn hình bình thường như không có gì, rồi bấm
     * Lưu lần nữa lên đúng đợt thu đang được job ghi dở.
     */
    const resumeBackgroundUpdateIfRunning = async () => {
        try {
            const res = await collectionAPIWithErrorHandling.getUpdateStatus(id);
            const state = res?.data?.data;

            if (state?.state === "QUEUED" || state?.state === "RUNNING") {
                setSubmitting(true);   // khoá thao tác y như lúc vừa bấm Lưu
                await waitForBackgroundUpdate(state.total);
                setSubmitting(false);
                return;
            }

            // Lỗi của lần lưu trước: người dùng đã đóng tab nên không thấy hộp thoại lúc
            // đó. Không báo ở đây thì họ mở lại màn, thấy mọi thứ bình thường và tưởng đã
            // lưu xong — trong khi thay đổi chưa hề được ghi.
            if (state?.state === "FAILED") {
                Modal.error({
                    title: "Lần cập nhật trước không thành công",
                    content: state.message || "Thay đổi chưa được lưu. Vui lòng kiểm tra lại và lưu lại.",
                });
            }
        } catch (e) {
            // Không lấy được trạng thái thì coi như không có việc nào chạy — đây chỉ là
            // lớp bảo vệ thêm, không được phép chặn việc mở màn.
        }
    };

    // Poll tiến độ job cập nhật đợt thu chạy nền.
    const waitForBackgroundUpdate = async (total) => {
        const POLL_MS = 3000;
        const MAX_MS = 30 * 60 * 1000;   // trần 30 phút, khớp timeout của job
        const startedAt = Date.now();

        setBgUpdate({ running: true, total: total || 0, message: "Đang chờ xử lý..." });

        while (Date.now() - startedAt < MAX_MS) {
            await new Promise((r) => setTimeout(r, POLL_MS));

            let state;
            try {
                const st = await collectionAPIWithErrorHandling.getUpdateStatus(id);
                state = st?.data?.data;
            } catch (e) {
                // Mất mạng tạm thời thì thử lại ở vòng sau, không bỏ dở
                continue;
            }

            if (state?.state === "DONE") {
                setBgUpdate({ running: false });
                message.success("Cập nhật đợt thu thành công!");
                fetchCollectionData();
                return;
            }

            if (state?.state === "FAILED") {
                setBgUpdate({ running: false });
                Modal.error({
                    title: "Cập nhật đợt thu không thành công",
                    content: state.message || "Đã có lỗi xảy ra, vui lòng lưu lại.",
                });
                return;
            }

            setBgUpdate({
                running: true,
                total: state?.total || total || 0,
                done: state?.done || 0,
                message: state?.message || "Đang cập nhật...",
            });
        }

        setBgUpdate({ running: false });
        Modal.warning({
            title: "Chưa nhận được kết quả",
            content: "Tiến trình cập nhật vẫn đang chạy ở hệ thống. Vui lòng tải lại trang sau ít phút để kiểm tra.",
        });
    };
    
    const handleTemplateSelect = (templateData) => {
        if (!templateData || !templateData.items || templateData.items.length === 0) {
          message.warning("Mẫu không có khoản thu nào");
          setTemplateModalVisible(false);
          return;
        }
    
        Modal.confirm({
          title: "Xác nhận áp dụng mẫu",
          content: `Bạn có chắc chắn muốn áp dụng mẫu "${templateData.name}" với ${templateData.items.length} khoản thu cho ${entries.length} dòng thu? Dữ liệu hiện tại sẽ bị ghi đè.`,
          okText: "Áp dụng",
          cancelText: "Hủy",
          onOk: () => {
            const newFees = templateData.items.map((item, index) => ({
                id: `K${index + 1}`,
                name: item.item_name,
                price: Number(item.unit_price) || 0,
                tax: Number(item.tax) || 0,        // ✅ THÊM
                unit: item.unit_name || ''         // ✅ THÊM
            }));
            
            setFees(newFees);
    
            // const currentData = form.getFieldValue("entryData") || {};
            // const newData = { ...currentData };
    
            // entries.forEach(entry => {
            //   const entryId = entry.id;
            //   const existingAccPaymentId = newData[entryId]?.acc_payment_id;
            //   const existingChargeTargetNumber = newData[entryId]?.charge_target_number || "";
            const newData = {};

            entries.forEach(entry => {
                const entryId = entry.id;
                // ✅ Đọc từ entryValues thay vì form
                const existingAccPaymentId = entryValues[entryId]?.acc_payment_id;
                const existingChargeTargetNumber = entryValues[entryId]?.charge_target_number || "";
                const existingDiscount = entryValues[entryId]?.discount_override || 0;
                
                // newData[entryId] = {
                //     discount_override: newData[entryId]?.discount_override || 0,
                //     charge_target_number: existingChargeTargetNumber,
                //     acc_payment_id: existingAccPaymentId
                // };
                newData[entryId] = {
                    discount_override: existingDiscount,
                    charge_target_number: existingChargeTargetNumber,
                    acc_payment_id: existingAccPaymentId
                };
                newFees.forEach(fee => {
                    newData[entryId][fee.id] = fee.price;
                });
            });
    
            // ✅ Sync vào entryValues để FeeInputCell hiển thị đúng
            setEntryValues(prev => {
                const updated = { ...prev };
                entries.forEach(entry => {
                    updated[entry.id] = {
                        ...(updated[entry.id] || {}),
                        ...(newData[entry.id] || {})
                    };
                });
                return updated;
            });

            message.info(`Đã áp dụng mẫu "${templateData.name}"`);
            setTemplateModalVisible(false);
          }
        });
    };

    // Handle form reset to original data
    const handleReset = () => {
        if (originalData) {
            fetchCollectionData();
            message.info("Đã khôi phục dữ liệu ban đầu");
        }
    };

    // Handle back navigation
    const handleBack = () => {
        navigate("/collections");
        message.info("Quay lại danh sách đợt thu");
    };
    
    
    const handleAddCustomer = () => {
        Modal.confirm({
          title: "Xác nhận",
          content: "Bạn có chắc chắn muốn chuyển trang tạo khách hàng mới không?",
          okText: "Đồng ý",
          cancelText: "Hủy",
          onOk: () => {
            navigate("/store-customer");
          },
        });
    };
    

    // Fee Name Edit Modal Component
    const FeeNameEditModal = (
        <Modal
            title="Cấu hình khoản thu"
            width="40%"
            open={feeNameModalVisible}
            onOk={saveFeeNameChange}
            onCancel={() => {
                setFeeNameModalVisible(false);
                setEditingFee(null);
                setTempFeeName("");
                setTempFeePrice(0);
                setApplyPriceToAll(false);
            }}
            okText="Lưu"
            cancelText="Hủy"
            className="collection-fee-modal"
        >
            <Form.Item
                label="Tên khoản thu"
                required
            >
                <Input
                    value={tempFeeName}
                    onChange={(e) => setTempFeeName(e.target.value)}
                    placeholder="Nhập tên khoản thu mới"
                    maxLength={50}
                    showCount
                />
            </Form.Item>
        
            {/* THÊM PHẦN NÀY */}
            <Form.Item
                label="Giá mặc định"
                required
                tooltip="Giá này sẽ được áp dụng cho khách hàng mới thêm vào"
            >
                <InputNumber
                    value={tempFeePrice}
                    onChange={(value) => setTempFeePrice(value || 0)}
                    min={0}
                    max={999999999}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                    placeholder="0"
                    style={{ width: "100%" }}
                    addonAfter="đ"
                />
            </Form.Item>
            
            <Row gutter={[16, 16]}>
                <Col lg={12} sm={24}>
                    {/* Input thuế - THAY ĐỔI PHẦN NÀY */}
                    <Form.Item
                        label="Thuế (%)"
                        tooltip="Thuế đã bao gồm trong giá"
                    >
                        <InputNumber
                            value={tempFeeTax}
                            onChange={(value) => setTempFeeTax(value || 0)} // THAY ĐỔI DÒNG NÀY
                            min={0}
                            max={100}
                            formatter={(value) => `${value}`}
                            placeholder="0"
                            style={{ width: "100%" }}
                            addonAfter="%"
                        />
                    </Form.Item>
                </Col>
                <Col lg={12} sm={24}>
                    <Form.Item
                        label="Đơn vị"
                        tooltip="Đơn vị"
                    >
                        <Input
                            value={tempUnit}
                            onChange={(e) => setTempUnit(e.target.value ?? '')}  // ✅ ĐÚNG
                            placeholder="Đơn vị khoản thu"
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* THÊM CHECKBOX */}
            <Form.Item>
                <Space direction="horizontal" style={{ width: '100%' }}>
                    <Switch
                        checked={applyPriceToAll}
                        onChange={(checked) => setApplyPriceToAll(checked)}
                    />
                    <Text>Áp dụng giá này cho tất cả khách hàng hiện tại</Text>
                </Space>
            </Form.Item>
        
            {/* THÊM CẢNH BÁO */}
            {applyPriceToAll && (
                <Alert
                    message="Bạn đang thay đổi giá cho TẤT CẢ khách hàng hiện tại. Hành động này sẽ ghi đè lên giá trị cũ."
                    type="warning"
                    icon={<ExclamationCircleOutlined />}
                />
            )}
        </Modal>
    );


    //   const stats = calculateStats();
    // const editable = isCollectionEditable();
    const editableRows = canEditRows();
    const editableMain = canEditMainInfo();
    // Đợt đã chạy: không về Nháp, không sửa thời gian (end_date là hạn mã giao dịch VCB/MB).
    // Cùng luật với CollectionService::assertRunningCollectionRules.
    const isRunning = !!originalData && originalData.status !== "PLANNED";
    const editable = editableRows; // For backward compatibility in Step 2/3 locations

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
                style={{
                display: "flex",
                justifyContent: "center",
                height: "80vh",
                alignItems: "center",
                }}>
                <Spin size="large" tip="Đang tải thông tin đợt thu..." />
            </div>
        );
    }

    return (
        <div className="collection-form-page">
            <div className="collection-page-header">
                <div className="collection-page-header-main">
                <Breadcrumb
                    separator=">"
                    items={[
                        {
                            href: "/",
                            title: (
                                <>
                                    <HomeOutlined /> Trang chủ
                                </>
                            ),
                        },
                        {
                            href: "/collections",
                            title: (
                                <>
                                    <DollarOutlined /> Đợt thu
                                </>
                            ),
                        },
                        {
                            title: (
                                <>
                                    <EditOutlined /> Chỉnh sửa
                                </>
                            ),
                        },
                    ]}
                />
                    <h2>Chỉnh sửa đợt thu</h2>
                    <p>{currentStepGuide.description}</p>
                </div>

                {/* Steps nằm chung dải với tiêu đề. Bản cũ còn một khối hero phía trên
                    lặp lại đúng những gì Steps đã nói, tốn hơn 100px ở mọi bước. */}
                <Steps
                    className="collection-stepper"
                    current={currentStep}
                    onChange={async (step) => {
                        if (submitting) return;
                        if (currentStep === 0 && step > 0) {
                            try {
                                await form.validateFields(['name', 'dateRange', 'collection_type', 'status']);
                                setCurrentStep(step);
                            } catch (_) {}
                        } else {
                            setCurrentStep(step);
                        }
                    }}
                    size="small"
                    items={[
                        { title: 'Thông tin' },
                        { title: 'Dòng thu' },
                        { title: 'Xác nhận' },
                    ]}
                />
            </div>

                {!editable && (
                    <Alert
                        message="Đợt thu không thể chỉnh sửa!"
                        description="Đợt thu này đang diễn ra, đã kết thúc hoặc bị vô hiệu hóa. Một số thông tin có thể không thể chỉnh sửa."
                        type="warning"
                        className="mb-6"
                        style={{ fontSize: 12 }}
                    />
                )}

                {bgUpdate.running && (
                    <Alert
                        message="Đang cập nhật ở chế độ nền"
                        description={
                            <>
                                <div>
                                    {bgUpdate.total
                                        ? `${bgUpdate.message} — ${(bgUpdate.done || 0).toLocaleString("vi-VN")}/${bgUpdate.total.toLocaleString("vi-VN")} dòng. Đợt thu lớn nên hệ thống xử lý nền, anh/chị giữ trang này mở tới khi có thông báo.`
                                        : bgUpdate.message}
                                </div>
                                {/* Tiến độ nhảy theo mốc đơn vị (backend cập nhật sau mỗi đơn vị
                                    xử lý xong), không mượt tới từng dòng. Đợt thu chỉ có một đơn
                                    vị thì sẽ nhảy thẳng từ 0 lên 100. */}
                                {bgUpdate.total > 0 && (
                                    <Progress
                                        percent={Math.round(((bgUpdate.done || 0) / bgUpdate.total) * 100)}
                                        size="small"
                                        status="active"
                                        style={{ marginTop: 8, marginBottom: 0 }}
                                    />
                                )}
                            </>
                        }
                        type="info"
                        showIcon
                        className="mb-6"
                    />
                )}

                {/* Tiến độ phải nằm ngay trong khối spinner này. Alert ở đầu trang cũng báo
                    đúng ngần ấy, nhưng người dùng bấm Lưu ở bước 3 tức cuối trang nên không
                    nhìn thấy nó — chỉ thấy vòng xoay và tưởng máy treo. */}
                <Spin
                    spinning={submitting}
                    tip={
                        bgUpdate.running ? (
                            <div style={{ marginTop: 8 }}>
                                <div style={{ fontWeight: 600 }}>
                                    {bgUpdate.message || "Đang xử lý nền"}
                                </div>
                                {bgUpdate.total > 0 && (
                                    <div style={{ fontSize: 13, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                                        {(bgUpdate.done || 0).toLocaleString("vi-VN")}/
                                        {bgUpdate.total.toLocaleString("vi-VN")} dòng
                                        {" · "}
                                        {Math.round(((bgUpdate.done || 0) / bgUpdate.total) * 100)}%
                                    </div>
                                )}
                                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.75 }}>
                                    Giữ trang này mở tới khi có thông báo
                                </div>
                            </div>
                        ) : (
                            "Đang cập nhật thông tin..."
                        )
                    }
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark="optional"
                        preserve={true}
                    >

                        {/* ── Bước 1: Thông tin đợt thu ── */}
                        <div style={{ display: currentStep !== 0 ? 'none' : 'block' }}>
                        <Row gutter={[24, 16]}>
                            <Col xs={24} lg={17} md={14}>
                                <Card
                                    title={
                                        <span>
                                            <span className="collection-card-icon blue"><CalendarOutlined /></span>
                                            Thông tin đợt thu
                                        </span>
                                    }
                                    className="collection-section-card collection-card-blue h-full"
                                >
                                    <Row gutter={[12, 0]}>
                                    {/* ── Nhóm 1: Thông tin cơ bản ── */}
                                    <Col xs={24} md={24}>
                                    <Form.Item
                                        name="name"
                                        label={<span className="font-medium">Tên đợt thu</span>}
                                        rules={[
                                            { required: true, message: "Vui lòng nhập tên đợt thu" },
                                            { max: 200, message: "Tên không được vượt quá 200 ký tự" },
                                        ]}
                                    >
                                        <Input
                                            placeholder="VD: Đợt thu tháng 11/2025"
                                            className="rounded-lg"
                                            disabled={!editableMain}
                                        />
                                    </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} md={12}>
                                    <Form.Item
                                        name="collection_category_id"
                                        label={<span className="font-medium">Loại phí</span>}
                                    >
                                        <Select
                                            placeholder="Chọn loại phí"
                                            allowClear
                                            showSearch
                                            // disabled={!editable}
                                            disabled={!editableMain}
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={categories.map(cat => ({
                                                value: cat.id,
                                                label: cat.name
                                            }))}
                                        />
                                    </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="collection_type"
                                            label="Nhóm đối tượng"
                                            rules={[{ required: true, message: "Vui lòng chọn nhóm đối tượng" }]}
                                        >
                                        <Radio.Group
                                            defaultValue={originalData.charge_target_type}
                                            // onChange={onChangeTarget}
                                            disabled={!editableMain}
                                        >
                                            {collectionTypes.map(item => (
                                                <Radio key={item.value} value={item.value} disabled={item.disabled}>
                                                    {item.label}
                                                </Radio>
                                            ))}
                                        </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="status"
                                            label={<span className="font-medium">Trạng thái</span>}
                                            rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
                                        >
                                            <Select
                                                placeholder="Chọn trạng thái"
                                                options={[
                                                    { label: "Nháp", value: "PLANNED", disabled: isRunning },
                                                    { label: "Đang hoạt động", value: "ONGOING" },
                                                ]}
                                                disabled={!editableMain}
                                            />
                                        </Form.Item>
                                    </Col>

                                    {/* ── Nhóm 2: Thời gian ── */}
                                    <Col xs={24}>
                                        <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '4px 0 12px' }}>
                                            Thời gian thu
                                        </Divider>
                                    </Col>
                                    <Col xs={24} md={12}>
                                    <Form.Item
                                        name="dateRange"
                                        label={<span className="font-medium">Khoảng thời gian</span>}
                                        extra={isRunning ? "Đợt thu đã chạy, không sửa được thời gian thu vì ảnh hưởng hạn mã thanh toán." : null}
                                        rules={[
                                            { required: true, message: "Vui lòng chọn thời gian thu" }
                                        ]}
                                    >
                                        <RangePicker
                                            className="w-full rounded-lg"
                                            format="DD/MM/YYYY"
                                            placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
                                            onChange={handleDateRangeChange}
                                            //disabled={!editable}
                                            disabled={!editableMain || isRunning}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                    <Form.Item
                                        name="send_date"
                                        label={<span className="font-medium">Hạn thu (tin nhắn nhận)</span>}
                                    >
                                        <DatePicker
                                            className="w-full rounded-lg"
                                            format="DD/MM/YYYY"
                                            placeholder="Chọn ngày"
                                            //disabled={!editable}
                                            disabled={!editableMain}
                                            style={{ width: "100%" }}
                                        />
                                    </Form.Item>
                                    </Col>
                                    
                                    {/* <Col xs={24} md={12}>
                                        <Form.Item
                                            name="invoice_cnf_id"
                                            label={<span className="font-medium">T/K phát hành hóa đơn</span>}
                                            rules={[
                                                { required: false, message: "Vui lòng chọn T/K phát hành hóa đơn" }
                                            ]}
                                        >
                                        <Select
                                            style={{ width: "100%" }}
                                            placeholder="Chọn tài khoản"
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={invoiceCnfs.map((item) => ({
                                                value: item.id,
                                                label: item.name,
                                            }))}
                                        />
                                        </Form.Item>
                                    </Col> */}

                                    {/* Nội dung chuyển khoản và Mô tả phần lớn đợt thu để trống,
                                        gom lại cho bước 1 gọn trong một màn. Đợt đang sửa mà đã có
                                        dữ liệu thì mở sẵn — giấu dữ liệu có thật là bẫy. */}
                                    <Col xs={24}>
                                        <Collapse
                                            size="small"
                                            className="collection-extra-collapse"
                                            defaultActiveKey={hasExtraInfo ? ["extra"] : []}
                                            items={[{
                                                key: "extra",
                                                label: (
                                                    <span className="collection-extra-label">
                                                        Nội dung chuyển khoản &amp; Mô tả đợt thu
                                                        <Tag color={hasExtraInfo ? "blue" : "default"} style={{ marginInlineEnd: 0 }}>
                                                            {hasExtraInfo ? "Đã nhập" : "Tùy chọn"}
                                                        </Tag>
                                                    </span>
                                                ),
                                                children: (
                                                    <>
                                                        <Form.Item
                                                            name="transfer_content"
                                                            label={<span className="font-medium">Nội dung chuyển khoản</span>}
                                                            tooltip="Nội dung hiển thị khi khách hàng quét mã chuyển khoản (Tối đa 30 ký tự)"
                                                        >
                                                            <TransferContentInput disabled={!editableMain} />
                                                        </Form.Item>

                                                        <Form.Item
                                                            name="description"
                                                            label={<span className="font-medium">Mô tả đợt thu</span>}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea
                                                                placeholder="Mô tả chi tiết về đợt thu này..."
                                                                rows={3}
                                                                className="rounded-lg"
                                                                maxLength={1000}
                                                                showCount
                                                                disabled={!editableMain}
                                                            />
                                                        </Form.Item>
                                                    </>
                                                ),
                                            }]}
                                        />
                                    </Col>

                                    </Row>
                                </Card>
                            </Col>

                            <Col xs={24} lg={7} md={10}>
                                <Card
                                    title={
                                        <span>
                                            <span className="collection-card-icon green"><EnvironmentOutlined /></span>
                                            Cấu hình gửi tin
                                        </span>
                                    }
                                    className="collection-section-card collection-card-green h-full"
                                >
                                    {/* Switch chính - luôn active */}
                                    <Form.Item
                                        name="is_send_zns"
                                        label={<span className="font-medium">Tin thông báo ZNS</span>}
                                        valuePropName="checked"
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Switch 
                                            checkedChildren="Bật" 
                                            unCheckedChildren="Tắt" 
                                            disabled={!editableMain}
                                        />
                                    </Form.Item>

                                    {(userType === "SHOP" || userType === "WARD_LEADER") && (
                                        <>
                                            <Form.Item
                                                name="is_locked"
                                                label={<span className="font-medium">Khóa chỉnh sửa cấp con</span>}
                                                valuePropName="checked"
                                                style={{ marginBottom: 12 }}
                                                extra={
                                                    originalData?.type_child === "CHILD"
                                                        ? "Quyền này do tài khoản cha cấu hình. Cấp con chỉ xem được trạng thái hiện tại."
                                                        : "Bật để chỉ tài khoản cha được sửa. Tắt để cho phép cấp con chỉnh sửa đợt thu của đơn vị mình."
                                                }
                                            >
                                                <Switch
                                                    checkedChildren="Khóa"
                                                    unCheckedChildren="Cho sửa"
                                                    disabled={!editable || originalData?.type_child === "CHILD"}
                                                />
                                            </Form.Item>

                                            <div
                                                style={{
                                                    background: isChildEditLocked ? "#FFF5E8" : "#EAF9F2",
                                                    border: `1px solid ${isChildEditLocked ? "#FFE0B8" : "#B8E8D0"}`,
                                                    borderRadius: 8,
                                                    padding: "10px 12px",
                                                    marginBottom: 14,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        color: isChildEditLocked ? "#A8681E" : "#128555",
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    {isChildEditLocked
                                                        ? "Cấp con đang bị khóa chỉnh sửa"
                                                        : "Cấp con được phép chỉnh sửa"}
                                                </div>
                                                <div style={{ fontSize: 12, color: "#475569" }}>
                                                    {isChildEditLocked
                                                        ? "Đợt thu này chỉ tài khoản cha xử lý cập nhật."
                                                        : "Nhân sự đơn vị con có thể vào màn chỉnh sửa đợt thu của đơn vị mình."}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Cấu hình nâng cao - disabled, sớm ra mắt */}
                                    <div style={{
                                        background: '#F8FAFC',
                                        border: '1px dashed #CBD5E1',
                                        borderRadius: 8,
                                        padding: '12px 14px',
                                        marginBottom: 14
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                            <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Cấu hình lịch gửi</span>
                                            <span style={{
                                                fontSize: 11,
                                                background: '#FFF5E8',
                                                color: '#A8681E',
                                                border: '1px solid #FFE0B8',
                                                borderRadius: 4,
                                                padding: '1px 6px'
                                            }}>⏰ Sắp ra mắt</span>
                                        </div>
                                        <Row gutter={[8, 8]}>
                                            <Col xs={24}>
                                                <Form.Item
                                                    name="send_schedule_at"
                                                    label={<span style={{ fontSize: 12 }}>Giờ gửi</span>}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <TimePicker
                                                        style={{ width: "100%" }}
                                                        placeholder="Giờ gửi"
                                                        onChange={onChangeSendScheduleAt}
                                                        format="HH:mm"
                                                        size="small"
                                                        // disabled={!isSendZns}
                                                        disabled
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Item
                                                    name="before_date"
                                                    label={<span style={{ fontSize: 12 }}>Trước hạn</span>}
                                                    rules={[{ type: "number", min: 0, message: "≥ 0" }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        placeholder="0"
                                                        style={{ width: "100%" }}
                                                        addonAfter="ngày"
                                                        size="small"
                                                        // disabled={!isSendZns}
                                                        disabled
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12}>
                                                <Form.Item
                                                    name="after_date"
                                                    label={<span style={{ fontSize: 12 }}>Sau hạn</span>}
                                                    rules={[{ type: "number", min: 1, message: "≥ 1" }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber
                                                        min={1}
                                                        placeholder="0"
                                                        style={{ width: "100%" }}
                                                        addonAfter="ngày"
                                                        size="small"
                                                        // disabled={!isSendZns}
                                                        disabled
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>

	                                    <Alert
	                                        message="Tin nhắn từ 22:00–06:00 sẽ được gửi vào sáng hôm sau"
	                                        type="info"
	                                        showIcon
	                                        icon={<ExclamationCircleOutlined />}
	                                        style={{ fontSize: 12 }}
	                                    />
	                                </Card>

	                            </Col>
	                        </Row>
                        </div>{/* /Bước 1 */}

                        {/* ── Bước 2: Thêm dòng thu ── */}
                        <div style={{ display: currentStep !== 1 ? 'none' : 'block' }}>
                        {editable && (
                            
                            /* Hai khối này chỉ dùng lúc thêm dòng. Đợt thu đã có dòng thì thu lại
                               thành một thanh mỏng để nhường chỗ cho bảng khoản thu bên dưới —
                               nhãn vẫn nói rõ tài khoản đang chọn, hoặc báo đỏ nếu chưa chọn. */
                            <Collapse
                                ghost
                                size="small"
                                className="collection-step2-collapse"
                                defaultActiveKey={entries.length === 0 ? ["add"] : []}
                                items={[{
                                    key: "add",
                                    label: (
                                        <span className="collection-step2-label">
                                            Thêm dòng thu
                                            <span className="sep">·</span>
                                            {selectedAccount ? (
                                                <span>Tài khoản: <strong>{selectedAccount.name}</strong></span>
                                            ) : (
                                                <Text type="danger" style={{ fontSize: 13 }}>Chưa chọn tài khoản thanh toán</Text>
                                            )}
                                        </span>
                                    ),
                                    children: (
                            <Row gutter={[24, 16]} style={{ marginTop: 0 }}>
                                <Col xs={24} sm={24} md={12} lg={12} xl={16}>
                                    <Card
                                        className="collection-section-card collection-card-purple"
                                        title={
                                            <span>
                                                <span className="collection-card-icon purple"><UserOutlined /></span>
                                                Thêm dòng thu khách hàng
                                            </span>
                                        }
                                    >
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <Row gutter={[12, 12]}>
                                                <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                                                    <TreeSelect
                                                        treeDataSimpleMode
                                                        style={{ width: '100%' }}
                                                        value={valueOrg}
                                                        styles={{
                                                            popup: { root: { maxHeight: 400, overflow: "auto" } },
                                                        }}
                                                        placeholder="Chọn đơn vị"
                                                        onChange={onChange}
                                                        treeData={treeData}
                                                        showSearch
                                                        filterTreeNode={(input, item) =>
                                                            item.title.toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        allowClear
                                                        treeDefaultExpandAll={false}
                                                    />
                                                </Col>
                            
                                                <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        placeholder="Lọc theo nhóm"
                                                        value={selectedGroup}
                                                        onChange={handleGroupChange}
                                                        disabled={!valueOrg || availableGroups.length === 0 || loadingGroup}
                                                        loading={loadingGroup}
                                                        allowClear
                                                        showSearch
                                                        filterOption={(input, option) =>
                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        options={availableGroups.map(group => ({
                                                            value: group.id,
                                                            label: group.name,
                                                        }))}
                                                    />
                                                </Col>
                        
                                                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                                    {ENABLE_ASSET_MODE && (
                                                        <div style={{ marginBottom: 12 }}>
                                                            <Radio.Group value={selectionMode} onChange={handleModeChangeSelection} buttonStyle="solid" size="small">
                                                                <Radio.Button value="CUSTOMER">Theo Khách hàng</Radio.Button>
                                                                <Radio.Button value="ASSET">Theo Tài sản</Radio.Button>
                                                            </Radio.Group>
                                                        </div>
                                                    )}
                                                    {selectionMode === "CUSTOMER" ? (
                                                        <Select
                                                            placeholder="Chọn khách hàng"
                                                            style={{ width: '100%' }}
                                                            showSearch
                                                            filterOption={(input, option) =>
                                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                            }
                                                            loading={loadingCus}
                                                            disabled={loadingCus || !valueOrg}
                                                            onSelect={(customerId) => addEntry(customerId, "")}
                                                            value={undefined}
                                                            options={availableCustomers.map(customer => ({
                                                                value: customer.id,
                                                                label: `${customer.name} - ${customer.phone}`,
                                                            }))}
                                                        />
                                                    ) : (
                                                        <Select
                                                            placeholder="Chọn tài sản (Phòng/Vị trí)"
                                                            style={{ width: '100%' }}
                                                            showSearch
                                                            filterOption={(input, option) =>
                                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                            }
                                                            loading={loadingAssets}
                                                            disabled={loadingAssets || !valueOrg}
                                                            onSelect={handleAssetSelect}
                                                            value={undefined}
                                                            options={availableAssets.map(asset => ({
                                                                value: asset.id,
                                                                label: `${asset.name} - Chủ: ${asset.customer?.name || 'N/A'}`,
                                                            }))}
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                            
                                            <Row gutter={[12, 12]}>
                                                {/* ── Nhóm hành động chính ── */}
                                                <Col xs={24}>
                                                    <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '0 0 8px' }}>
                                                        Thêm nhanh
                                                    </Divider>
                                                </Col>
                                                <Col xs={24} sm={12} md={12} lg={12}>
                                                    <Button
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={addAllEntries}
                                                        disabled={
                                                            !editable ||
                                                            availableCustomers.length === 0 ||
                                                            availableCustomers.every((c) =>
                                                                entries.some((e) => e.customer_id === c.id)
                                                            )
                                                        }
                                                        block
                                                    >
                                                        Thêm tất cả
                                                    </Button>
                                                </Col>
                                                
                                                <Col xs={24} sm={12} md={12} lg={12}>
                                                    <Button
                                                        type="dashed"
                                                        icon={<PlusOutlined />}
                                                        onClick={handleAddCustomer}
                                                        block
                                                    >
                                                        Khách hàng mới
                                                    </Button>
                                                </Col>

                                                {/* ── Nhóm công cụ ── */}
                                                <Col xs={24}>
                                                    <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '4px 0 8px' }}>
                                                        Công cụ
                                                    </Divider>
                                                </Col>
                                                <Col xs={24} sm={12} md={12} lg={8}>
                                                    <Button
                                                        type="dashed"
                                                        icon={<FileSyncOutlined />}
                                                        onClick={handleExportTemplate}
                                                        block
                                                    >
                                                        Xuất mẫu Excel
                                                    </Button>
                                                </Col>
                                                <Col xs={24} sm={12} md={12} lg={8}>
                                                    <Upload
                                                        fileList={importFileList}
                                                        beforeUpload={(file) => {
                                                            handleImportExcel(file);
                                                            return false; // Prevent auto-upload
                                                        }}
                                                        onChange={({ fileList }) => setImportFileList(fileList)}
                                                        showUploadList={false}
                                                        accept=".xlsx,.xls"
                                                        style={{ width: '100%' }}
                                                    >
                                                        <Button
                                                        type="dashed"
                                                        icon={<FileSyncOutlined />}
                                                        block
                                                        style={{ width: '100%' }}
                                                        >
                                                        Nhập từ Excel
                                                        </Button>
                                                    </Upload>
                                                </Col>
                        
                                                <Col xs={24} sm={12} md={12} lg={8}>
                                                    <Button
                                                        type="dashed"
                                                        icon={<PlusOutlined />}
                                                        onClick={addFeeType}
                                                        disabled={entries.length === 0}
                                                        block
                                                    >
                                                        Thêm khoản thu
                                                    </Button>
                                                </Col>

                                                <Col xs={24} sm={12} md={12} lg={8}>
                                                    <Button
                                                    type="dashed"
                                                    icon={<FileSyncOutlined />}
                                                    onClick={() => setTemplateModalVisible(true)}
                                                    disabled={entries.length === 0}
                                                    block
                                                    >
                                                    Chọn mẫu thu
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                                    <Card 
                                        title={
                                            <span>
                                                <span className="collection-card-icon" style={{ background: '#F0F6FD', marginRight: 8 }}>💳</span>
                                                Tài khoản thanh toán
                                            </span>
                                        }
                                        className={`collection-section-card collection-payment-card ${!selectedAccount ? 'collection-payment-card-warning' : ''}`}
                                    >
                                    {!selectedAccount && (
                                        <Alert
                                            message="Vui lòng chọn tài khoản thanh toán trước khi thêm khách hàng"
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 14, fontSize: 12 }}
                                        />
                                    )}
	                                    <PaymentAccountSelector
	                                        accounts={accounts}
	                                        onSelect={handleSelectAccount}
	                                        selectedAccountId={selectedAccount?.id}
	                                    />

	                                    {entries.length > 0 && (
	                                        <div style={{ marginTop: 12, borderTop: '1px dashed #E2E8F0', paddingTop: 10 }}>
	                                            <Text type="secondary" style={{ fontSize: 12 }}>
	                                                Chọn tài khoản chỉ áp cho khách thêm mới. Đổi cho dòng đã có thì bấm:
	                                            </Text>
	                                            <Button
	                                                size="small"
	                                                style={{ marginTop: 8 }}
	                                                disabled={!selectedAccount}
	                                                onClick={() => applyAccountTo("tat_ca")}
	                                            >
	                                                Áp cho toàn bộ {entries.length} dòng
	                                            </Button>

	                                            {/* Soát trước khi lưu: tiền của đợt này chảy về đâu, mỗi nơi bao nhiêu dòng */}
	                                            <div style={{ marginTop: 10, fontSize: 12 }}>
	                                                <Text type="secondary">Tiền sẽ về:</Text>
	                                                {accountBreakdown.map((item) => (
	                                                    <div key={item.accId ?? 'chua-chon'} style={{ marginTop: 4 }}>
	                                                        <Tag color={item.accId ? 'blue' : 'error'} style={{ marginRight: 6 }}>
	                                                            {item.soDong} dòng
	                                                        </Tag>
	                                                        {item.ten}
	                                                    </div>
	                                                ))}
	                                            </div>
	                                        </div>
	                                    )}
	                                    </Card>

	                                </Col>
	                            </Row>
                                    ),
                                }]}
                            />
	                        )}

                        <Card
                            className="collection-section-card collection-card-orange"
                            style={{ marginTop: 20 }}
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>
                                        <span className="collection-card-icon orange"><DollarOutlined /></span>
                                        Chi tiết các khoản thu
                                        <Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>
                                            (Giá đã bao gồm thuế)
                                        </Text>
                                    </span>
                                    <Space wrap>
	                                        <Text type="secondary" style={{ fontSize: 13 }}>
	                                            Hiển thị: <strong>{visibleEntryCount}</strong> / <strong>{entries.length}</strong> khách hàng
	                                        </Text>
	                                    </Space>
	                                </div>
	                            }
                        >
                            {entries.length > 0 && (
                                <div className="collection-filter-toolbar">
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Input
                                                placeholder="Tìm theo tên, SĐT, tài sản..."
                                                value={filterText}
                                                onChange={(e) => setFilterText(e.target.value)}
                                                allowClear
                                                prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
                                            />
                                        </Col>
                                        
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Lọc theo đơn vị"
                                                value={filterOrgId}
                                                onChange={setFilterOrgId}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={[
                                                    { value: null, label: 'Tất cả đơn vị' },
                                                    ...uniqueOrgs.map(org => ({
                                                        value: org.id,
                                                        label: org.name
                                                    }))
                                                ]}
                                            />
                                        </Col>
                                        
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Button
                                                type={sortByOrg ? "primary" : "default"}
                                                icon={<EnvironmentOutlined />}
                                                onClick={() => setSortByOrg(!sortByOrg)}
                                                style={{ width: '100%' }}
                                            >
                                                {sortByOrg ? "Đang nhóm" : "Nhóm theo đơn vị"}
                                            </Button>
                                        </Col>
                                        {(zeroEntries.length > 0 || zeroFilterIds) && (
                                            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                                <ZeroFeeFilterButton
                                                    count={zeroEntries.length}
                                                    active={!!zeroFilterIds}
                                                    onToggle={toggleZero}
                                                />
                                            </Col>
                                        )}
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Button
                                                style={{ width: '100%' }}
                                                onClick={() => {
                                                    setFilterText("");
                                                    setFilterOrgId(null);
                                                    setSortByOrg(true);
                                                    clearZero();
                                                }}
                                                disabled={!filterText && !filterOrgId && !zeroFilterIds && sortByOrg}
                                            >
                                                Xóa bộ lọc
                                            </Button>
                                        </Col>
                                        {/* Lọc ra một khối đơn vị rồi áp tài khoản cho đúng khối đó — cách gọn
                                            nhất cho đợt thu gồm nhiều khối dùng tài khoản khác nhau. */}
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Button
                                                style={{ width: '100%' }}
                                                disabled={!selectedAccount || (!filterText && !filterOrgId)}
                                                onClick={() => applyAccountTo("dang_loc")}
                                            >
                                                Áp tài khoản cho dòng đang lọc
                                            </Button>
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            {/* <Button
                                                style={{ width: '100%' }}
                                                icon={<CopyOutlined />}
                                                onClick={handleExportExcel}
                                                disabled={entries.length === 0}
                                            >
                                                Xuất Excel
                                            </Button> */}
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            {(filterText || filterOrgId || zeroFilterIds) && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    <FilterOutlined /> Đang lọc
                                                </Text>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                            )}
                            
                            {/* {entries.length > 0 ? ( */}
                            {tableDataSource.length > 0 ? (
                            (() => {
                                const totalWidth = columns.reduce((sum, col) => {
                                const width = typeof col.width === 'string'
                                    ? parseInt(col.width) || 150
                                    : (col.width || 150);
                                return sum + width;
                                }, 0);

                                const rowHeight = 70;
                                const maxHeight = tableMaxHeight;
                                const listHeight = Math.min(tableDataSource.length * rowHeight, maxHeight);
            
                                return (
                                <div
                                    className="collection-table-container"
                                    style={{ "--collection-table-width": `${totalWidth + 50}px` }}
                                >
                                    <div className="collection-table-header-viewport" ref={tableHeaderRef}>
                                    <div
                                        className="collection-table-header"
                                        style={{ minWidth: totalWidth + 50 }}
                                    >
                                        {columns.map((col, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                            width: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                            minWidth: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                            flexShrink: 0,
                                            textAlign: col.align || 'left',
                                            padding: '0 8px',
                                            borderRight: idx !== columns.length - 1 ? '1px solid #E2E8F0' : 'none', // ← thêm đường kẻ phân chia
                                            boxSizing: 'border-box', // tránh tràn width
                                            // Ô ghim phải cao bằng cả hàng tiêu đề (có ô hai
                                            // dòng vì kèm thuế/đơn vị) và mang nền đặc trùng
                                            // tông tiêu đề, không thì phần cột trượt qua bên
                                            // dưới vẫn lòi ra ở khoảng hở trên và dưới.
                                            ...(col.fixed === 'left' && {
                                                position: 'sticky',
                                                left: 0,
                                                alignSelf: 'stretch',
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: '#eceff4',
                                                zIndex: 11,
                                                borderRight: '1px solid #cbd5e1',
                                                boxShadow: '6px 0 6px -4px rgba(15, 23, 42, 0.12)',
                                            }),
                                            ...(col.fixed === 'right' && {
                                                position: 'sticky',
                                                right: 0,
                                                alignSelf: 'stretch',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                background: '#eceff4',
                                                zIndex: 11,
                                                borderLeft: '1px solid #cbd5e1',
                                                boxShadow: '-6px 0 6px -4px rgba(15, 23, 42, 0.12)',
                                            })
                                            }}
                                        >
                                            {typeof col.title === 'object' ? col.title : col.title}
                                        </div>
                                        ))}
                                    </div>
            
                                    </div>

                                    <List
                                        className="collection-table-body"
                                        outerRef={tableBodyRef}
                                        height={listHeight}
                                        itemCount={tableDataSource.length}
                                        itemSize={70}
                                        width="100%"
                                        itemKey={(index) => tableDataSource[index]?.key || index}
                                        itemData={{ tableDataSource, columns }}
                                    >
                                        {/* {({ index, style }) => {
                                        const record = tableDataSource[index];
                                        // Nếu là header row
                                        if (record.isHeader) {
                                            return (
                                            <div
                                                className="collection-group-header"
                                                style={style}
                                            >
                                                <EnvironmentOutlined />
                                                {record.orgName} ({record.itemCount} khách hàng)
                                            </div>
                                            );
                                        }
                                        
                                        return (
                                            <div
                                            className={`collection-table-row ${index % 2 === 0 ? 'collection-table-row-even' : 'collection-table-row-odd'}`}
                                            style={style}
                                            >
                                            {columns.map((col, colIdx) => (
                                                <div
                                                key={colIdx}
                                                style={{
                                                    width: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                    minWidth: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                    flexShrink: 0,
                                                    padding: '0 8px',
                                                    // textAlign: col.align || 'right',
                                                    textAlign: 'right !important',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    ...(col.fixed === 'left' && {
                                                        position: 'sticky !important',
                                                        left: 0,
                                                        backgroundColor: index % 2 === 0 ? '#fff' : '#F8FAFC',
                                                        zIndex: 9,
                                                        borderRight: '1px solid #E2E8F0'
                                                    }),
                                                    ...(col.fixed === 'right' && {
                                                        position: 'sticky',
                                                        right: 0,
                                                        backgroundColor: index % 2 === 0 ? '#fff' : '#F8FAFC',
                                                        zIndex: 9,
                                                        borderLeft: '1px solid #E2E8F0'
                                                    }),
                                                    ...(col.align === 'right' && {
                                                        justifyContent: 'flex-end',
                                                    }),
                                                    ...(col.align === 'left' && {
                                                        justifyContent: 'flex-start',
                                                    }),
                                                    ...(col.align === 'center' && {
                                                        justifyContent: 'center',
                                                    })
                                                }}
                                                >
                                                    {col.render ? col.render(record[col.dataIndex], record, index) : record[col.dataIndex]}
                                                </div>
                                            ))}
                                            </div>
                                        );
                                        }} */}
                                        {CollectionRow}
                                    </List>
                                </div>
                                );
                            })()
                            ) : (
                            <div className="collection-empty-state">
                                <UserOutlined />
                                <h4>Chưa có khách hàng nào</h4>
                                <p>Vui lòng thêm khách hàng để bắt đầu</p>
                            </div>
                            )}

                            {/* Summary Bar - Tổng kết */}
                            {/* Trước đây bọc Form.Item shouldUpdate để bắt thay đổi của entryData
                                trong store. Nay getCollectionSummary() đọc thẳng entryValues nên
                                state đổi là vẽ lại — giữ wrapper chỉ làm thanh tổng đứng số cũ. */}
                            {entries.length > 0 && (
	                                (() => {
	                                        const { grandSubtotal, grandDiscount, grandTotal } =
	                                            getCollectionSummary();

	                                        return (
	                                            <div className="collection-summary">
                                                <Row gutter={[24, 12]} align="middle">
                                                    <Col xs={24} sm={6}>
                                                        <div className="collection-summary-stat">
                                                            <div className="stat-label">Số lượng</div>
                                                            <div className="stat-value blue">
                                                                <UserOutlined style={{ marginRight: 4 }} />
                                                                {entries.length}
                                                                <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>khách hàng</span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={6}>
                                                        <div className="collection-summary-stat">
                                                            <div className="stat-label">Tổng phí</div>
                                                            <div className="stat-value green">
                                                                {grandSubtotal.toLocaleString('vi-VN')} đ
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={6}>
                                                        <div className="collection-summary-stat">
                                                            <div className="stat-label">Tổng giảm trừ</div>
                                                            <div className="stat-value orange">
                                                                -{grandDiscount.toLocaleString('vi-VN')} đ
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} sm={6}>
                                                        <div className="collection-summary-highlight">
                                                            <div className="stat-label">Tổng tiền thu</div>
                                                            <div className="stat-value">
                                                                <DollarOutlined style={{ marginRight: 4 }} />
                                                                {grandTotal.toLocaleString('vi-VN')} đ
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        );
                                    })()
                            )}
                        </Card>

                        </div>{/* /Bước 2 */}

                        {/* ── Bước 3: Xác nhận & Lưu ── */}
                        <div style={{ display: currentStep !== 2 ? 'none' : 'block' }}>
                            <Card
                            title={
                                <span>
                                <span className="collection-card-icon blue"><CalendarOutlined /></span>
                                Xác nhận cập nhật đợt thu
                                </span>
                            }
                            className="collection-section-card collection-card-blue"
                            >
	                            <Form.Item shouldUpdate>
	                                {({ getFieldValue }) => {
	                                const name = getFieldValue('name');
	                                const categoryId = getFieldValue('collection_category_id');
	                                const catObj = categories.find(c => c.id === categoryId);
	                                const statusVal = getFieldValue('status');
	                                const statusLabel = statusVal === 'PLANNED' ? 'Nháp' : (statusVal === 'ONGOING' ? 'Đang hoạt động' : statusVal);
	                                const dateRange = getFieldValue('dateRange');
	                                const isLocked = getFieldValue('is_locked') ?? true;
	                                const transferContent = getFieldValue('transfer_content');
	                                const description = getFieldValue('description');
	                                const { grandSubtotal, grandDiscount, grandTotal } =
	                                    getCollectionSummary();
	                                const rowSt = { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F8FAFC' };
	                                const lbSt = { color: '#64748B', fontSize: 13 };
	                                const vlSt = { fontWeight: 500, fontSize: 13 };
	                                return (
                                    <Row gutter={[32, 0]}>
                                    <Col xs={24} md={15}>
                                        <div style={rowSt}>
                                        <span style={lbSt}>Tên đợt thu</span>
                                        <span style={vlSt}>{name || '—'}</span>
                                        </div>
                                        {catObj && <div style={rowSt}><span style={lbSt}>Loại phí</span><span style={vlSt}>{catObj.name}</span></div>}
                                        <div style={rowSt}>
                                        <span style={lbSt}>Trạng thái</span>
                                        <span style={{ ...vlSt, color: statusVal === 'ONGOING' ? '#128555' : '#475569' }}>{statusLabel}</span>
                                        </div>
                                        <div style={rowSt}>
                                        <span style={lbSt}>Quyền sửa cấp con</span>
                                        <span style={vlSt}>{isLocked ? 'Chỉ tài khoản cha được sửa' : 'Cho phép cấp con chỉnh sửa'}</span>
                                        </div>
                                        {dateRange?.[0] && <div style={rowSt}><span style={lbSt}>Thời gian</span><span style={vlSt}>{dateRange[0].format('DD/MM/YYYY')} → {dateRange?.[1]?.format('DD/MM/YYYY')}</span></div>}
                                        {transferContent && (
                                        <div style={rowSt}>
                                            <span style={lbSt}>Nội dung chuyển khoản</span>
                                            <span style={{ ...vlSt, fontFamily: 'monospace', background: '#F8FAFC', padding: '2px 8px', borderRadius: 4 }}>{transferContent}</span>
                                        </div>
                                        )}
                                        {description && <div style={{ ...rowSt, borderBottom: 'none' }}><span style={lbSt}>Mô tả</span><span style={{ ...vlSt, maxWidth: '60%', textAlign: 'right' }}>{description}</span></div>}
                                    </Col>
	                                    <Col xs={24} md={9}>
	                                        <div className="collection-summary" style={{ marginTop: 0 }}>
	                                        <div className="collection-summary-stat">
	                                            <div className="stat-label">Số lượng</div>
	                                            <div className="stat-value blue">
	                                            <UserOutlined style={{ marginRight: 4 }} />{entries.length}
	                                            <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>khách hàng</span>
	                                            </div>
	                                        </div>
	                                        <div className="collection-summary-stat">
	                                            <div className="stat-label">Tổng phí</div>
	                                            <div className="stat-value green">{grandSubtotal.toLocaleString('vi-VN')} đ</div>
	                                        </div>
	                                        <div className="collection-summary-stat">
	                                            <div className="stat-label">Tổng giảm trừ</div>
	                                            <div className="stat-value orange">-{grandDiscount.toLocaleString('vi-VN')} đ</div>
	                                        </div>
	                                        <div className="collection-summary-highlight">
	                                            <div className="stat-label">Tổng tiền thu</div>
	                                            <div className="stat-value"><DollarOutlined style={{ marginRight: 4 }} />{grandTotal.toLocaleString('vi-VN')} đ</div>
                                        </div>
                                        </div>
                                    </Col>
                                    </Row>
                                );
                                }}
                            </Form.Item>
                            </Card>
                        </div>{/* /Bước 3 */}

                        {/* Action Buttons */}
                        <div className="collection-actions">
                            <Row justify="space-between" align="middle">
                                <Col>
                                    {currentStep === 0 ? (
	                                        <Button htmlType="button" icon={<ArrowLeftOutlined />} onClick={handleBack} className="collection-btn-back">
	                                            Quay lại
	                                        </Button>
	                                    ) : (
	                                        <Button htmlType="button" icon={<ArrowLeftOutlined />} onClick={handlePrevStep} className="collection-btn-back">
	                                            Bước trước
	                                        </Button>
	                                    )}
                                </Col>
                                {/* Số dòng và tổng tiền ngay trên thanh nút: bước 2 bảng dài,
                                    bản cũ phải cuộn ngược lên khối tổng hợp mới đối chiếu được. */}
                                <Col flex="auto">
                                    {currentStep > 0 && entries.length > 0 && (
                                        <span className="collection-actions-summary">
                                            <span><strong>{entries.length.toLocaleString("vi-VN")}</strong> dòng</span>
                                            <span className="sep">·</span>
                                            <span>Tổng <strong>{getCollectionSummary().grandTotal.toLocaleString("vi-VN")} đ</strong></span>
                                        </span>
                                    )}
                                </Col>
                                <Col>
                                    <Space>
                                        {currentStep === 0 && (
	                                            <Button
	                                                htmlType="button"
	                                                type="default"
	                                                onClick={handleReset}
	                                                className="collection-btn-reset"
                                                disabled={!originalData}
                                            >
                                                Khôi phục
                                            </Button>
                                        )}
                                        {currentStep < 2 ? (
	                                            <Button
	                                                key="next-step"
	                                                htmlType="button"
	                                                type="primary"
	                                                icon={<ArrowRightOutlined />}
	                                                onClick={handleNextStep}
	                                            >
	                                                Tiếp theo
	                                            </Button>
	                                        ) : (
	                                            <Button
	                                                key="submit-step"
	                                                type="primary"
	                                                htmlType="submit"
                                                loading={submitting}
                                                icon={<SaveOutlined />}
                                                className="collection-btn-submit"
                                                // bgUpdate.running: đợt thu đang được job ghi dở
                                                // (có thể do phiên trước, bắt lại được sau khi F5).
                                                // Cho bấm Lưu lúc này là ghi đè lên dữ liệu đang ghi.
                                                disabled={!isCollectionEditable() || bgUpdate.running}
                                            >
                                                {bgUpdate.running
                                                    ? "Đang xử lý nền..."
                                                    : submitting ? "Đang cập nhật..." : "Cập nhật"}
                                            </Button>
                                        )}
                                    </Space>
                                </Col>
                            </Row>
                        </div>
                        {FeeNameEditModal}
                    </Form>
                </Spin>

            { templateModalVisible && 
                <FeeTemplateModal
                    visible={templateModalVisible}
                    onCancel={() => setTemplateModalVisible(false)}
                    onSuccess={handleTemplateSelect}
                />
            }

            {/* ── Import Summary Modal ── */}
            <Modal
                open={importSummaryVisible}
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18, color: '#128555' }}>✅</span>
                        Nhập Excel thành công
                    </span>
                }
                onCancel={() => setImportSummaryVisible(false)}
                onOk={() => setImportSummaryVisible(false)}
                okText="Đã hiểu"
                cancelButtonProps={{ style: { display: 'none' } }}
                width={420}
            >
                <div style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#EAF9F2', border: '1px solid #B8E8D0', borderRadius: 8,
                            padding: '12px 16px'
                        }}>
                            <span style={{ color: '#128555', fontWeight: 500 }}>🟢 Dòng thêm mới</span>
                            <span style={{ fontSize: 22, fontWeight: 700, color: '#128555' }}>{importSummary.added}</span>
                        </div>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#F0F6FD', border: '1px solid #B8D7F4', borderRadius: 8,
                            padding: '12px 16px'
                        }}>
                            <span style={{ color: '#005AAC', fontWeight: 500 }}>🔵 Dòng cập nhật</span>
                            <span style={{ fontSize: 22, fontWeight: 700, color: '#0070D7' }}>{importSummary.updated}</span>
                        </div>
                        {importSummary.skipped?.length > 0 && (
                            <div style={{
                                background: '#FFF5E8', border: '1px solid #FFE0B8', borderRadius: 8,
                                padding: '12px 16px'
                            }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    marginBottom: 8
                                }}>
                                    <span style={{ color: '#A8681E', fontWeight: 500 }}>⚠ Dòng bỏ qua</span>
                                    <span style={{ fontSize: 22, fontWeight: 700, color: '#A8681E' }}>{importSummary.skipped.length}</span>
                                </div>
                                {/* Nói rõ dòng nào và vì sao. Chỉ đưa con số thì người nhập
                                    không biết mình vừa mất dữ liệu ở đâu để mà sửa file. */}
                                <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 13 }}>
                                    {importSummary.skipped.map((item, i) => (
                                        <div key={i} style={{
                                            display: 'flex', gap: 8,
                                            padding: '4px 0',
                                            borderTop: i === 0 ? 'none' : '1px dashed #FFE0B8'
                                        }}>
                                            <span style={{ color: '#A8681E', fontWeight: 600, minWidth: 60 }}>
                                                Dòng {item.row}
                                            </span>
                                            <span style={{ flex: 1 }}>
                                                {item.customer || '-'}
                                                <span style={{ color: '#64748B' }}> — {item.reason}</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <Alert
                        message="Dữ liệu đã được tải vào bảng. Vui lòng kiểm tra và bấm Cập nhật để lưu."
                        type="info"
                        showIcon
                        style={{ marginTop: 16, fontSize: 12 }}
                    />
                </div>
            </Modal>
        </div>
    );
}
